import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Plus, MapPin, FileText, Package2, X, Clock, Car, MessageSquare, Upload, Folder, RefreshCw, ClipboardList, FileSearch, LayoutDashboard, Menu, Target, type LucideIcon } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import ActiveJobTimerBeforeActionModal, { type BeforeActionType } from './ActiveJobTimerBeforeActionModal';

import { useNavigate, useLocation } from 'react-router-dom';
import CreateBookingModal from './CreateBookingModal';
import DocumentPickerModal from './documents/DocumentPickerModal';
import DocumentFillModal from './documents/DocumentFillModal';
import { getClockProcedures, type ProcedureTriggerEvent } from '../lib/documents';
import {
  clockIn,
  clockOut,
  startBreak,
  endBreak,
  getTimeclockStatus,
  getOwnTimeclockStatus,
  onTimeclockChanged,
  TimeclockError,
  type TimeclockEntry,
} from '../lib/timeclock';
import toast from 'react-hot-toast';
import CreateProposalModal from './CreateProposalModal';
import CreateProjectModal from './CreateProjectModal';
import WithdrawStockModal from './WithdrawStockModal';
import UploadReceiptModalWrapper from './expenses/UploadReceiptModalWrapper';
import AddManualExpenseModalWrapper from './expenses/AddManualExpenseModalWrapper';
import CreateSubscriptionModal from './CreateSubscriptionModal';
// Horizontal nav imports - REMOVED

import { useBusiness } from '../contexts/BusinessContext';
import { useAppSystem } from '../hooks/useAppSystem';
import { supabase } from '../lib/supabase';
import { isCoreMessagesEnabled, fetchCoreUnreadCounts } from '../lib/messagesCore';
import { readStatusSeen, writeStatusSeen } from './agents/BotAvatar';
import { useAuth } from '../hooks/useAuth';
import { useApplicationSettingsContext } from '../contexts/ApplicationSettingsContext';
import { getResources } from '../lib/resources';

import CreateMobileBookingModal from './CreateMobileBookingModal';
import CreateTaskModal from './CreateTaskModal';
import ScheduleFieldQuoteModal from './ScheduleFieldQuoteModal';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import NexusOverlay from './NexusOverlay';
import NexusVoiceIntroModal from './NexusVoiceIntroModal';
import { useNexusAiOnboarding } from '../hooks/useNexusAiOnboarding';
import { useVoiceHelp } from '../contexts/VoiceHelpContext';
import { useAgentBeta } from '../contexts/AgentBetaContext';
import { MOBILE_BOTTOM_BAR, MOBILE_BOTTOM_BAR_HEIGHT_REM } from '../lib/featureFlags';
import { useIsMobileViewport } from '../hooks/useIsMobileViewport';
import { useDismissOverlayProtectingCalendarCreate } from '../hooks/useDismissOverlayProtectingCalendarCreate';
import { hapticLight, hapticMedium } from '../lib/haptics';
import { hub } from './agents/api';
import NexusFace, { VoiceOrb } from './NexusFace';
import type { Customer } from '../lib/customers';
import CrmEmbeddedCreateOpportunityModal from './admin-dashboard/CrmEmbeddedCreateOpportunityModal';
import LeadCreateChoiceModal from './crm/LeadCreateChoiceModal';
import BusinessCardScanModal from './crm/BusinessCardScanModal';
import SalesPinDropModal from './sales-map/SalesPinDropModal';

const fabMenuShellStyle: React.CSSProperties = {
  borderRadius: 14,
  background: 'rgba(255, 255, 255, 0.78)',
  border: '0.5px solid rgba(0, 0, 0, 0.08)',
  boxShadow:
    '0 0 0 0.5px rgba(0,0,0,0.04), 0 10px 40px rgba(0,0,0,0.18), 0 2px 10px rgba(0,0,0,0.08)',
  backdropFilter: 'blur(40px) saturate(180%)',
  WebkitBackdropFilter: 'blur(40px) saturate(180%)',
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif',
};

/**
 * Bottom-bar variant. The frosted look above relies on backdrop-filter, but in
 * bar mode this popover is nested inside the bar's own backdrop-filter — a
 * parent filter creates a new backdrop root, so the child blur samples nothing
 * and the 78% white just reads as see-through. Opaque surface instead.
 */
const fabMenuShellStyleOpaque: React.CSSProperties = {
  ...fabMenuShellStyle,
  background: '#ffffff',
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
};

/** Row matching calendar / messages create popover (icon left, label right). */
function FabMenuRow({
  label,
  description,
  icon: Icon,
  onClick,
  disabled,
  iconClassName = 'bg-[#007AFF] text-white shadow-sm shadow-[#007AFF]/30',
}: {
  label: string;
  description?: string;
  icon?: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  iconClassName?: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      data-assistant-fab-option={label}
      disabled={disabled}
      onClick={onClick}
      className={`flex w-full items-center gap-3 px-3 py-2.5 text-left touch-manipulation transition-colors ${
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'hover:bg-black/[0.04] active:bg-black/[0.08]'
      }`}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      {Icon ? (
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] ${
            disabled ? 'bg-gray-200 text-gray-400 shadow-none' : iconClassName
          }`}
        >
          <Icon className="h-[15px] w-[15px]" strokeWidth={2.25} />
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span
          className={`block text-[15px] font-semibold leading-tight truncate ${
            disabled ? 'text-black/40' : 'text-black/85'
          }`}
        >
          {label}
        </span>
        {description ? (
          <span className="block text-[12px] text-black/40 mt-0.5 leading-snug">
            {description}
          </span>
        ) : null}
      </span>
    </button>
  );
}

// NavItem interface - REMOVED (was only used for horizontal nav)

interface FloatingActionButtonProps {
  permissionLevel?: 'manage' | 'view' | 'disabled';
  onSuccess?: () => Promise<void>;
  onOpenAISetupAssistant?: () => void;
  /** Apps-menu state, so the bottom bar's menu block can drive and reflect it. */
  sidebarOpen?: boolean;
  onSetSidebarOpen?: (open: boolean) => void;
  /** Lets the shell know the bar is showing (it hides the header hamburger). */
  onBottomBarActiveChange?: (active: boolean) => void;
}

/**
 * One flat block in the mobile bottom bar. Deliberately minimal — icon over a
 * small label, no pill or gradient — so the protruding centre circle is the
 * only thing that draws the eye.
 */
function BarTab({
  icon,
  label,
  onClick,
  onPointerDown,
  active = false,
  disabled = false,
  badge,
  ariaLabel,
  ariaPressed,
  anchor,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  onPointerDown?: () => void;
  active?: boolean;
  disabled?: boolean;
  badge?: number;
  ariaLabel?: string;
  ariaPressed?: boolean;
  anchor?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={onPointerDown}
      disabled={disabled}
      aria-label={ariaLabel ?? label}
      aria-pressed={ariaPressed}
      data-assistant-anchor={anchor}
      className={`relative flex flex-1 flex-col items-center justify-center gap-0.5 transition-colors disabled:opacity-40 touch-manipulation ${
        active ? 'text-blue-600' : 'text-gray-500 active:text-gray-900'
      }`}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <span className="relative flex h-[22px] items-center justify-center">
        {icon}
        {badge && badge > 0 ? (
          <span className="absolute -right-2.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white">
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </span>
      <span className="text-[10px] font-medium leading-none whitespace-nowrap">{label}</span>
    </button>
  );
}

/** iOS-style overshoot spring used by the AI fan. */
const FAN_SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

/**
 * Voice mode is parked. The AI button opens Agents directly and does not
 * offer Voice mode. Flip this to bring the Voice / Agents fan back.
 */
const AI_VOICE_FAN_ENABLED = false;

/** How long a finger rests on the AI button before it counts as a hold. */
const AI_HOLD_MS = 450;

/**
 * Press-and-hold on a button. Fires `onHold` once the finger has rested for
 * AI_HOLD_MS, then swallows the click that lands on release so the tap action
 * does not run as well. Spread `handlers` on the button and gate its onClick
 * with `consumeHold()`.
 */
function useHoldAction(onHold: () => void) {
  const timer = useRef<number | null>(null);
  const fired = useRef(false);
  const onHoldRef = useRef(onHold);
  onHoldRef.current = onHold;
  const clear = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  }, []);
  useEffect(() => clear, [clear]);
  const handlers = {
    onPointerDown: (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      fired.current = false;
      clear();
      timer.current = window.setTimeout(() => {
        timer.current = null;
        fired.current = true;
        onHoldRef.current();
      }, AI_HOLD_MS);
    },
    onPointerUp: clear,
    onPointerCancel: clear,
    onPointerLeave: clear,
    // iOS shows a callout / magnifier on a long press; this is a control, not text.
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  };
  /** True when the click that just arrived is the release of a hold. */
  const consumeHold = () => {
    if (!fired.current) return false;
    fired.current = false;
    return true;
  };
  return { handlers, consumeHold };
}

/**
 * One option that springs out of the centre AI circle. Collapsed it sits
 * behind the circle at 30% scale; open it arcs up and out with a slight
 * overshoot, label fading in a beat after the circle lands.
 */
function AiFanOption({
  open,
  x,
  y,
  delayMs,
  icon,
  label,
  onClick,
  className,
  shadow,
  ariaLabel,
  anchor,
  side = 'up',
  mark,
}: {
  open: boolean;
  x: number;
  y: number;
  delayMs: number;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  className: string;
  shadow: string;
  ariaLabel?: string;
  anchor?: string;
  /** `up` springs above the circle. `left` slides out beside it. */
  side?: 'up' | 'left';
  /** Status dot, shown once the option has sprung out. */
  mark?: React.ReactNode;
}) {
  return (
    <div
      className={`absolute left-1/2 flex flex-col items-center motion-reduce:transition-none ${side === 'left' ? 'top-1' : '-top-5'}`}
      style={{
        // Centre the 3rem option on the 3.5rem circle, then fan out.
        transform: open
          ? side === 'left'
            ? `translate(calc(-50% + ${x}px), 0) scale(1)`
            : `translate(calc(-50% + ${x}px), ${y}px) scale(1)`
          : side === 'left'
            ? 'translate(-50%, 0) scale(0.3)'
            : 'translate(-50%, 0.25rem) scale(0.3)',
        opacity: open ? 1 : 0,
        pointerEvents: open ? 'auto' : 'none',
        transition: open
          ? `transform 460ms ${FAN_SPRING} ${delayMs}ms, opacity 180ms ease-out ${delayMs}ms`
          : 'transform 240ms cubic-bezier(0.4, 0, 1, 1), opacity 160ms ease-in',
        willChange: 'transform, opacity',
      }}
      aria-hidden={!open}
    >
      <button
        type="button"
        tabIndex={open ? 0 : -1}
        onClick={onClick}
        aria-label={ariaLabel ?? label}
        data-assistant-anchor={anchor}
        className={`relative flex h-12 w-12 items-center justify-center rounded-full text-white ring-[3px] ring-white transition-transform active:scale-90 touch-manipulation ${className}`}
        style={{ boxShadow: shadow, WebkitTapHighlightColor: 'transparent' }}
      >
        {icon}
        {mark}
      </button>
      <span
        className="mt-1.5 rounded-full bg-gray-900/90 px-2 py-0.5 text-[11px] font-semibold leading-none text-white shadow-sm backdrop-blur whitespace-nowrap"
        style={{
          opacity: open ? 1 : 0,
          transform: open ? 'translateY(0)' : 'translateY(-4px)',
          transition: open
            ? `opacity 200ms ease-out ${delayMs + 140}ms, transform 300ms ${FAN_SPRING} ${delayMs + 140}ms`
            : 'opacity 100ms ease-in',
        }}
      >
        {label}
      </span>
    </div>
  );
}

/** Corner dot for the AI button: green while bots work, red when one is waiting on you. */
type AgentNotice = { id: string; chat_id: string; kind: string; title: string; body: string; created_at?: string };

const DISMISSED_NOTICE_LIMIT = 40;

function dismissedNoticeStorageKey(businessId: string) {
  return `nexus-agent-notice-dismissed:${businessId}`;
}

function loadDismissedNoticeIds(businessId: string): Set<string> {
  try {
    const raw = localStorage.getItem(dismissedNoticeStorageKey(businessId));
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

function saveDismissedNoticeIds(businessId: string, ids: Set<string>) {
  try {
    localStorage.setItem(
      dismissedNoticeStorageKey(businessId),
      JSON.stringify([...ids].slice(-DISMISSED_NOTICE_LIMIT))
    );
  } catch {
    // Private mode can refuse storage. The in-memory set still hides it this visit.
  }
}

/** A notice stays down once its row was dismissed, or the owner has already opened that chat. */
function noticeHandled(businessId: string | null, row: AgentNotice): boolean {
  if (!row?.id) return true;
  if (businessId && loadDismissedNoticeIds(businessId).has(row.id)) return true;
  if (!businessId || !row.chat_id) return false;
  const seenAt = readStatusSeen(businessId)[row.chat_id];
  const seenMs = seenAt ? Date.parse(seenAt) : NaN;
  if (!Number.isFinite(seenMs)) return false;
  // A push replay has no timestamp. Opening the chat already covered the notice on screen.
  if (!row.created_at) return Date.now() - seenMs < 15 * 60 * 1000;
  const createdMs = Date.parse(row.created_at);
  return Number.isFinite(createdMs) && createdMs <= seenMs;
}

/** One speech bubble on the AI button. A newer notice replaces it. */
function AgentSpeechBubble({ notice, onOpen, onDismiss, className, tail = 'down' }: { notice: AgentNotice; onOpen: () => void; onDismiss: () => void; className: string; tail?: 'down' | 'right' }) {
  return (
    <div className={className}>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
        className="relative w-full rounded-2xl bg-gray-900 px-3 py-2.5 pr-8 text-left text-white shadow-lg"
      >
        <p className="text-[12px] font-semibold leading-4">{notice.title}</p>
        {notice.body ? <p className="mt-0.5 line-clamp-3 text-[12px] font-normal leading-4 text-white/80">{notice.body}</p> : null}
        {tail === 'right' ? (
          <span className="absolute left-full top-1/2 -translate-y-1/2 border-y-8 border-l-8 border-y-transparent border-l-gray-900" />
        ) : (
          <span className="absolute left-1/2 top-full -translate-x-1/2 border-x-8 border-t-8 border-x-transparent border-t-gray-900" />
        )}
      </button>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onDismiss();
        }}
        className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full text-white/70 hover:bg-white/15 hover:text-white"
        aria-label="Dismiss"
        title="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function AgentWorkMark({ signal }: { signal: 'working' | 'blocked' }) {
  if (signal === 'blocked') {
    return <span className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-rose-500" />;
  }
  return (
    <span className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
      <span className="relative inline-flex h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-400" />
    </span>
  );
}

export default function FloatingActionButton({
  permissionLevel = 'manage',
  onSuccess,
  onOpenAISetupAssistant,
  sidebarOpen = false,
  onSetSidebarOpen,
  onBottomBarActiveChange,
}: FloatingActionButtonProps) {
  const { selectedBusiness } = useBusiness();
  // Docs "Complete a document" FAB option toggle
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) { setDocumentsFabEnabled(false); return; }
    let active = true;
    supabase
      .from('businesses')
      .select('documents_fab_enabled')
      .eq('id', selectedBusiness.id)
      .maybeSingle()
      .then(({ data }) => { if (active) setDocumentsFabEnabled((data as any)?.documents_fab_enabled === true); });
    return () => { active = false; };
  }, [selectedBusiness?.id]);
  const { user } = useAuth();
  const { isPageDisabled } = useApplicationSettingsContext();
  const { canAccess, isAdmin, canViewAdminDashboard } = usePermissionCheck();
  // On the native app the Control Centre lives in this FAB stack (above messages)
  // instead of the top navigation bar.
  const isNativeApp = Capacitor.isNativePlatform();
  const { coreApps, installedAddons } = useAppSystem();
  const navigate = useNavigate();
  const location = useLocation();
  const { openAgents } = useAgentBeta();
  const messagesButtonVisible =
    !isPageDisabled('messages') &&
    canAccess.messages &&
    location.pathname !== '/messages' &&
    location.pathname !== '/calls-beta';

  // Platform workspaces (Instagram / Facebook / Google Business) let the live
  // grid own the screen — no floating FAB stack overlapping it.
  const onMarketingPlatformView =
    location.pathname === '/marketing' &&
    /(?:^\?|[?&])view=(google-business|facebook|instagram)(?:&|$)/.test(location.search);

  // AI voice mode toggle — sits at the top of this FAB stack.
  // Admin-only: resources only get it when they've been granted admin.
  // Business owners (role admin) and resources marked is_admin see the AI
  // centre button. A regular resource keeps Control Centre in that slot.
  const aiVoiceAllowed = canViewAdminDashboard;
  const showMessagesTab =
    !isPageDisabled('messages') && canAccess.messages;
  const { status: voiceStatus, processing: voiceProcessing, speaking: voiceSpeaking, surface: voiceSurface, startVoice, stopVoice, prewarmVoice, chatTarget: voiceChatTarget } = useVoiceHelp();
  // A bot-page conversation uses the same session but must not light up this control.
  const voiceOn = (voiceStatus === 'active' || voiceStatus === 'connecting') && voiceSurface !== 'chat';
  const voiceSurfaceRef = useRef<'app' | 'chat'>('app');

  // First-use gate for Voice (and Help): intro → free 1M tokens → AI terms.
  const {
    loaded: aiOnboardingLoaded,
    needsOnboarding: needsAiOnboarding,
    termsAccepted: aiTermsAccepted,
    welcomeClaimed: aiWelcomeClaimed,
    showIntro: showVoiceIntro,
    setShowIntro: setShowVoiceIntro,
    pendingAction: aiPendingAction,
    setPendingAction: setAiPendingAction,
    requestAccess: requestAiAccess,
    markComplete: markAiOnboardingComplete,
  } = useNexusAiOnboarding();

  const voiceBotIdRef = useRef<string | null>(null);
  const voiceChatIdRef = useRef<string | null>(null);
  const voiceLeadRef = useRef<{ opportunityId?: number; name?: string }>({});
  const startVoiceSession = useCallback((surface: 'app' | 'chat' = 'app') => {
    const installedAppNames = [...(coreApps || []), ...(installedAddons || [])]
      .map((a) => a.app?.name)
      .filter((name): name is string => Boolean(name));
    const lead = voiceLeadRef.current;
    void startVoice(installedAppNames, selectedBusiness?.id, {
      surface,
      botId: surface === 'chat' ? voiceBotIdRef.current || undefined : undefined,
      chatId: surface === 'chat' ? voiceChatIdRef.current || undefined : undefined,
      leadOpportunityId: lead.opportunityId,
      leadName: lead.name,
    });
  }, [coreApps, installedAddons, startVoice, selectedBusiness?.id]);

  // Pre-mint the voice session (no token charge until used) so the mic tap
  // connects near-instantly: on app open, on returning to the foreground, and
  // as a final head start the moment the finger lands on the mic button.
  // The bot chat the owner is looking at right now, so its call button gets the
  // same head start as the app button. Set by the Agents workspace.
  const chatPrewarmRef = useRef<{ botId: string; chatId: string | null } | null>(null);
  const prewarmVoiceSession = useCallback(() => {
    if (!aiVoiceAllowed || !selectedBusiness?.id || needsAiOnboarding) return;
    const installedAppNames = [...(coreApps || []), ...(installedAddons || [])]
      .map((a) => a.app?.name)
      .filter((name): name is string => Boolean(name));
    // The app-wide voice button is parked behind the fan flag; chat calls are live regardless.
    if (AI_VOICE_FAN_ENABLED) prewarmVoice(installedAppNames, selectedBusiness.id);
    const chat = chatPrewarmRef.current;
    if (chat) prewarmVoice(installedAppNames, selectedBusiness.id, { botId: chat.botId, chatId: chat.chatId });
  }, [aiVoiceAllowed, coreApps, installedAddons, prewarmVoice, selectedBusiness?.id, needsAiOnboarding]);

  useEffect(() => {
    prewarmVoiceSession();
    const onVisible = () => {
      if (!document.hidden) prewarmVoiceSession();
    };
    document.addEventListener('visibilitychange', onVisible);
    // Agents workspace announces which bot chat is open (or none).
    const onChatTarget = (event: Event) => {
      const detail = (event as CustomEvent<{ botId?: string; chatId?: string } | null>).detail;
      chatPrewarmRef.current = detail?.botId ? { botId: detail.botId, chatId: detail.chatId || null } : null;
      if (chatPrewarmRef.current) prewarmVoiceSession();
    };
    window.addEventListener('assistant:prewarmVoice', onChatTarget);
    // The minted session lives 10 minutes — re-mint on a 4-minute tick (the
    // prewarm itself skips while the cache is still fresh) so a tap after the
    // app has been open a while never falls back to the slow path.
    const tick = setInterval(() => {
      if (!document.hidden) prewarmVoiceSession();
    }, 4 * 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('assistant:prewarmVoice', onChatTarget);
      clearInterval(tick);
    };
  }, [prewarmVoiceSession]);

  const handleVoiceToggle = () => {
    if (voiceOn) {
      stopVoice();
      return;
    }
    if (!requestAiAccess('voice')) return;
    startVoiceSession();
  };

  const handleAiOnboardingAccepted = () => {
    markAiOnboardingComplete();
    setAiPendingAction(null);
    // Voice FAB only starts voice; Help is gated separately in App.tsx.
    startVoiceSession(voiceSurfaceRef.current);
  };

  // "Hey Siri, talk to Nexus": the iOS App Intent deep-links assistant/voice
  // (warm start → 'assistant:startVoice' event) and stamps a Preferences flag
  // (cold start → checked here once the business context is ready). Both
  // paths start the exact session the mic button starts.
  const siriVoicePendingRef = useRef(false);
  // A bot-chat call asked for before the first-use check finished (cold load
  // straight into a chat). Replayed once the check is in; never shown the
  // app-wide voice intro, which is a different mode.
  const chatVoicePendingRef = useRef<{ botId?: string; chatId?: string } | null>(null);
  useEffect(() => {
    let cancelled = false;
    const tryStart = (surface: 'app' | 'chat' = 'app', botId?: string, chatId?: string, leadOpportunityId?: number, leadName?: string) => {
      if (cancelled || voiceOn) { siriVoicePendingRef.current = false; return; }
      // Voice is admin-only — Siri/deep-link starts are gated like the button.
      if (!aiVoiceAllowed) { siriVoicePendingRef.current = false; return; }
      if (!selectedBusiness?.id) { siriVoicePendingRef.current = true; return; }
      siriVoicePendingRef.current = false;
      if (surface === 'chat' && !aiOnboardingLoaded) { chatVoicePendingRef.current = { botId, chatId }; return; }
      chatVoicePendingRef.current = null;
      voiceSurfaceRef.current = surface;
      voiceBotIdRef.current = surface === 'chat' ? botId || null : null;
      voiceChatIdRef.current = surface === 'chat' ? chatId || null : null;
      voiceLeadRef.current = { opportunityId: leadOpportunityId, name: leadName };
      // Same AI onboarding gate as the mic button — accepting starts the session.
      // A bot-chat call is not the app-wide voice mode, so it never shows that intro;
      // the chat itself is already behind the Agents terms.
      if (needsAiOnboarding && surface !== 'chat') { setAiPendingAction('voice'); setShowVoiceIntro(true); return; }
      const installedAppNames = [...(coreApps || []), ...(installedAddons || [])]
        .map((a) => a.app?.name)
        .filter((name): name is string => Boolean(name));
      void startVoice(installedAppNames, selectedBusiness.id, {
        surface,
        botId: surface === 'chat' ? botId : undefined,
        chatId: surface === 'chat' ? chatId : undefined,
        leadOpportunityId,
        leadName,
      });
    };
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ surface?: string; botId?: string; chatId?: string; leadOpportunityId?: number; leadName?: string }>).detail;
      tryStart(detail?.surface === 'chat' ? 'chat' : 'app', detail?.botId, detail?.chatId, detail?.leadOpportunityId, detail?.leadName);
    };
    window.addEventListener('assistant:startVoice', handler);
    if (siriVoicePendingRef.current) tryStart();
    if (chatVoicePendingRef.current && aiOnboardingLoaded) {
      const pending = chatVoicePendingRef.current;
      tryStart('chat', pending.botId, pending.chatId);
    }
    if (Capacitor.isNativePlatform()) {
      void (async () => {
        try {
          const { Preferences } = await import('@capacitor/preferences');
          const { value } = await Preferences.get({ key: 'assistant_pending_voice' });
          if (value === '1') {
            await Preferences.remove({ key: 'assistant_pending_voice' });
            tryStart();
          }
        } catch { /* Siri hand-off is best-effort */ }
      })();
    }
    return () => {
      cancelled = true;
      window.removeEventListener('assistant:startVoice', handler);
    };
  }, [selectedBusiness?.id, coreApps, installedAddons, voiceOn, startVoice, needsAiOnboarding, aiOnboardingLoaded, setAiPendingAction, setShowVoiceIntro, aiVoiceAllowed]);
  // Stack slots are 3.5rem apart: plus button, then messages, then control
  // centre (native), then voice on top.
  const voiceButtonBottomRem =
    3.5 * (1 + (messagesButtonVisible ? 1 : 0) + (isNativeApp && selectedBusiness ? 1 : 0));
  
  // Check if apps are installed
  const isCalendarAppInstalled = coreApps.some(app => app.app?.slug === 'calendar') || 
                                 installedAddons.some(app => app.app?.slug === 'calendar');
  const isMobileCalendarAppInstalled = coreApps.some(app => app.app?.slug === 'mobile-calendar') || 
                                      installedAddons.some(app => app.app?.slug === 'mobile-calendar');
  const isProposalsAppInstalled = coreApps.some(app => app.app?.slug === 'proposals') || 
                                 installedAddons.some(app => app.app?.slug === 'proposals');
  const isProjectsAppInstalled = coreApps.some(app => app.app?.slug === 'projects') || 
                                installedAddons.some(app => app.app?.slug === 'projects');
  const isInventoryAppInstalled = coreApps.some(app => app.app?.slug === 'inventory') || 
                                 installedAddons.some(app => app.app?.slug === 'inventory');
  const isPayrollAppInstalled = coreApps.some(app => app.app?.slug === 'payroll') || 
                               installedAddons.some(app => app.app?.slug === 'payroll');
  const isExpensesAppInstalled = coreApps.some(app => app.app?.slug === 'expenses') || 
                                installedAddons.some(app => app.app?.slug === 'expenses');
  const isServicesAppInstalled = coreApps.some(app => app.app?.slug === 'services') || 
                                installedAddons.some(app => app.app?.slug === 'services');
  const isCrmAppInstalled = coreApps.some(app => app.app?.slug === 'crm') ||
                            installedAddons.some(app => app.app?.slug === 'crm');
  const isMarketingAppInstalled = coreApps.some(app => app.app?.slug === 'marketing') ||
                                  installedAddons.some(app => app.app?.slug === 'marketing');
  
  // App settings state - start with false to prevent flash
  const [showLocationBookingInFAB, setShowLocationBookingInFAB] = useState(false);
  const [showMobileBookingInFAB, setShowMobileBookingInFAB] = useState(false);
  const [showProposalInFAB, setShowProposalInFAB] = useState(false);
  const [showProjectInFAB, setShowProjectInFAB] = useState(false);
  const [showWithdrawStockInFAB, setShowWithdrawStockInFAB] = useState(false);
  const [showClockInFromFAB, setShowClockInFromFAB] = useState(true); // Default to true
  const [showExpenseInFAB, setShowExpenseInFAB] = useState(false);
  const [showSubscriptionsInFAB, setShowSubscriptionsInFAB] = useState(false);
  const [documentsFabEnabled, setDocumentsFabEnabled] = useState(false);
  const [showDocumentPicker, setShowDocumentPicker] = useState(false);
  const [showTasksInFAB, setShowTasksInFAB] = useState(false);
  const [showFieldQuotesInFAB, setShowFieldQuotesInFAB] = useState(false);
  const [showOpportunityInFAB, setShowOpportunityInFAB] = useState(false);
  const [showSalesPinInFAB, setShowSalesPinInFAB] = useState(false);
  const [appSettingsLoaded, setAppSettingsLoaded] = useState(false);
  
  const [showBookingOptions, setShowBookingOptions] = useState(false);
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] = useState(false);
  const [isCreateProposalModalOpen, setIsCreateProposalModalOpen] = useState(false);
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
  const [isWithdrawStockModalOpen, setIsWithdrawStockModalOpen] = useState(false);
  const [isAddExpenseModalOpen, setIsAddExpenseModalOpen] = useState(false);
  const [isManualExpenseModalOpen, setIsManualExpenseModalOpen] = useState(false);
  const [manualExpensePreselection, setManualExpensePreselection] = useState<{
    bookingId?: string | null;
    projectId?: string | null;
  } | null>(null);
  const [isCreateSubscriptionModalOpen, setIsCreateSubscriptionModalOpen] = useState(false);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isScheduleFieldQuoteFromFabOpen, setIsScheduleFieldQuoteFromFabOpen] = useState(false);
  const [isCreateOpportunityModalOpen, setIsCreateOpportunityModalOpen] = useState(false);
  const [aiCardScanEnabled, setAiCardScanEnabled] = useState(false);
  const [isLeadChoiceOpen, setIsLeadChoiceOpen] = useState(false);
  const [isCardScanOpen, setIsCardScanOpen] = useState(false);
  const [isSalesPinModalOpen, setIsSalesPinModalOpen] = useState(false);

  const [clockedIn, setClockedIn] = useState(false);
  const [isClockBusy, setIsClockBusy] = useState(false);
  const [onBreak, setOnBreak] = useState(false);
  const [currentPayrollEntryId, setCurrentPayrollEntryId] = useState<string | null>(null);
  const [resourceId, setResourceId] = useState<string | null>(null);
  // Document procedure gating (mandatory docs at clock events)
  const [procedureQueue, setProcedureQueue] = useState<string[]>([]);
  const [activeProcedureTemplateId, setActiveProcedureTemplateId] = useState<string | null>(null);
  const procedureProceedRef = useRef<null | (() => void | Promise<void>)>(null);
  const procedureCompletingRef = useRef(false);
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const [startTime, setStartTime] = useState<Date | null>(null);
  
  // Active job timers block clock out / break (FAB) - show prompt to stop timers first
  const [fabActiveJobTimerModalOpen, setFabActiveJobTimerModalOpen] = useState(false);
  const [fabPendingAction, setFabPendingAction] = useState<BeforeActionType | null>(null);
  const [fabActiveJobTimerEntries, setFabActiveJobTimerEntries] = useState<{ booking_id: string }[]>([]);
  const [fabActionModalLoading, setFabActionModalLoading] = useState(false);
  
  // Unviewed missed calls count
  const [unviewedMissedCallsCount, setUnviewedMissedCallsCount] = useState(0);

  // Function to count unviewed missed calls
  const countUnviewedMissedCalls = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      const { count, error } = await supabase
        .from('call_history')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', selectedBusiness.id)
        .eq('direction', 'inbound')
        .eq('status', 'missed')
        .eq('viewed', false);

      if (error) {
        console.error('[FAB BADGE] Error counting unviewed missed calls:', error);
      } else {
        const missedCount = count || 0;
        setUnviewedMissedCallsCount(missedCount);
        console.log('[FAB BADGE] 📞 Unviewed missed calls count:', missedCount);
      }
    } catch (error) {
      console.error('[FAB BADGE] Error in countUnviewedMissedCalls:', error);
    }
  };

  // Count unviewed missed calls on mount and business change
  useEffect(() => {
    if (selectedBusiness?.id) {
      countUnviewedMissedCalls();
    }
  }, [selectedBusiness?.id]);

  // Set up real-time subscription for call updates to update badge
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    const channel = supabase
      .channel(`call_badge_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'call_history',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        (payload) => {
          // Check if this is a viewed status change
          if (payload.eventType === 'UPDATE' && payload.new && payload.old) {
            const oldViewed = payload.old.viewed;
            const newViewed = payload.new.viewed;
            
            if (oldViewed === false && newViewed === true) {
              // Call marked as viewed - update badge
            }
          }
          
          // Recount when any call changes (INSERT, UPDATE, DELETE)
          countUnviewedMissedCalls();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedBusiness?.id]);
  const [breakStartTime, setBreakStartTime] = useState<Date | null>(null);
  const [compensationType, setCompensationType] = useState<'hourly' | 'flat_rate' | 'commission' | null>(null);
  const fabRef = useRef<HTMLDivElement>(null);
  const [isCreateMobileBookingModalOpen, setIsCreateMobileBookingModalOpen] = useState(false);
  
  
  // Message count state for badge display
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  
  // AI Assistant state
  const [isNexusOverlayOpen, setIsNexusOverlayOpen] = useState(false);
  const stopSpeechRef = useRef<(() => void) | null>(null);
  
  // Track Messages conversation state for mobile FAB hiding
  const [messagesConversationOpen, setMessagesConversationOpen] = useState(false);

  const isMobileViewport = useIsMobileViewport();
  // The whole cluster hides on a Messages conversation, on platform
  // workspaces, and on the Agents page at desktop width. The phone bar stays.
  const onAgentsPage = location.pathname === '/agents';
  const fabClusterHidden =
    (location.pathname === '/messages' && messagesConversationOpen) ||
    onMarketingPlatformView ||
    (onAgentsPage && !isMobileViewport);
  /** EXPERIMENT: lay the four buttons out as a bottom bar instead of a stack. */
  const bottomBarActive = MOBILE_BOTTOM_BAR && isMobileViewport && !fabClusterHidden;

  // Page content and fixed full-bleed shells read this to stay clear of the bar.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      '--bottom-nav-height',
      bottomBarActive
        ? `calc(${MOBILE_BOTTOM_BAR_HEIGHT_REM}rem + env(safe-area-inset-bottom, 0px))`
        : '0px',
    );
    return () => root.style.setProperty('--bottom-nav-height', '0px');
  }, [bottomBarActive]);

  useEffect(() => {
    onBottomBarActiveChange?.(bottomBarActive);
  }, [bottomBarActive, onBottomBarActiveChange]);


  // Listen for localStorage changes to track Messages conversation state
  useEffect(() => {
    const checkMessagesConversationState = () => {
      const isOpen = localStorage.getItem('messages_conversation_open') === 'true';
      setMessagesConversationOpen(isOpen);
    };

    // Check initial state
    checkMessagesConversationState();

    // Listen for storage events (when other tabs change localStorage)
    window.addEventListener('storage', checkMessagesConversationState);
    
    // Listen for custom events (when same tab changes localStorage)
    window.addEventListener('messagesConversationChanged', checkMessagesConversationState);

    return () => {
      window.removeEventListener('storage', checkMessagesConversationState);
      window.removeEventListener('messagesConversationChanged', checkMessagesConversationState);
    };
  }, []);

  // Mirror the signed-in user's shift from the shared timeclock engine, and
  // re-sync whenever any surface (booking prompt, day view, kiosk) changes it.
  const applyTimeclockEntry = useCallback((entry: TimeclockEntry | null) => {
    if (entry) {
      setClockedIn(true);
      setCurrentPayrollEntryId(entry.id);
      setStartTime(new Date(entry.clock_in));
      const breakRunning = !!entry.break_start && !entry.break_end;
      setOnBreak(breakRunning);
      setBreakStartTime(breakRunning ? new Date(entry.break_start as string) : null);
    } else {
      setClockedIn(false);
      setOnBreak(false);
      setCurrentPayrollEntryId(null);
      setStartTime(null);
      setBreakStartTime(null);
    }
  }, []);

  useEffect(() => {
    if (!selectedBusiness?.id || !user?.id) return;
    let cancelled = false;

    const checkClockInStatus = async () => {
      try {
        const status = await getOwnTimeclockStatus(selectedBusiness.id);
        if (cancelled || !status) return;
        setResourceId(status.resource.id);
        setCompensationType(status.resource.compensation_type as 'hourly' | 'flat_rate' | 'commission');
        applyTimeclockEntry(status.entry);
      } catch {
        // Silent - expected for accounts with no team-member record
      }
    };

    checkClockInStatus();
    const unsubscribe = onTimeclockChanged(() => { checkClockInStatus(); });
    return () => { cancelled = true; unsubscribe(); };
  }, [selectedBusiness?.id, user?.id, applyTimeclockEntry]);

  // Add timer effect
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    
    if (clockedIn && startTime) {
      intervalId = setInterval(() => {
        const now = new Date();
        let diff = now.getTime() - startTime.getTime();
        
        // Subtract break time if on break
        if (onBreak && breakStartTime) {
          diff = diff - (now.getTime() - breakStartTime.getTime());
        }
        
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        setElapsedTime(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [clockedIn, startTime, onBreak, breakStartTime]);

  // Fetch initial unread message count and set up realtime subscription for badge display
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    // Function to fetch current unread count (excludes muted email senders)
    const fetchUnreadCount = async () => {
      try {
        if (!supabase) {
          console.error('[FAB BADGE] Supabase client not initialized');
          return;
        }

        // Core tables: the same number the Messages nav badge shows.
        if (await isCoreMessagesEnabled(selectedBusiness.id)) {
          const counts = await fetchCoreUnreadCounts(selectedBusiness.id);
          setUnreadMessagesCount(counts.unread);
          return;
        }

        // First, get muted email senders for this business
        const { data: mutedSenders, error: mutedError } = await supabase
          .from('muted_email_senders')
          .select('email_address')
          .eq('business_id', selectedBusiness.id);
        
        if (mutedError) {
          console.error('[FAB BADGE] Error fetching muted senders:', mutedError);
        }
        
        const mutedEmails = new Set((mutedSenders || []).map(m => m.email_address.toLowerCase().trim()));

        // Get all unread messages (not just count, so we can filter)
        const { data: unreadMessages, error } = await supabase
          .from('all_messages')
          .select('id, message_type, from_number')
          .eq('business_id', selectedBusiness.id)
          .eq('read', 'unread')
          .eq('direction', 'inbound'); // Only count inbound unread messages

        if (error) {
          console.error('[FAB BADGE] Error fetching unread count:', error);
          return;
        }

        // Filter out messages from muted email senders
        const nonMutedMessages = (unreadMessages || []).filter(msg => {
          if (msg.message_type !== 'email') return true; // Include non-email messages
          // For emails, check if sender is muted
          const senderEmail = (msg.from_number || '').toLowerCase().trim();
          // Extract email from "Name <email>" format
          const emailMatch = senderEmail.match(/<([^>]+)>/);
          const normalizedEmail = emailMatch ? emailMatch[1] : senderEmail;
          return !mutedEmails.has(normalizedEmail);
        });

        const newCount = nonMutedMessages.length;
        setUnreadMessagesCount(newCount);
      } catch (error) {
        console.error('[FAB BADGE] Error fetching unread count:', error);
      }
    };

    // Fetch initial count
    fetchUnreadCount();

    // Set up realtime subscription with more specific event handling
    const subscription = supabase
      .channel(`unread_messages_fab_badge_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'all_messages',
          filter: `business_id=eq.${selectedBusiness.id}` // Only listen to messages for this business
        },
        (payload) => {
          console.log('[FAB BADGE] 📨 Received database event:', {
            eventType: payload.eventType,
            messageId: (payload.new as any)?.id || (payload.old as any)?.id,
            direction: (payload.new as any)?.direction || (payload.old as any)?.direction,
            read: (payload.new as any)?.read || (payload.old as any)?.read
          });
          
          // Add a small delay to ensure database consistency
          setTimeout(() => {
            fetchUnreadCount();
          }, 100);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'muted_email_senders',
          filter: `business_id=eq.${selectedBusiness.id}` // Only listen to muted senders for this business
        },
        (payload) => {
          console.log('[FAB BADGE] 🔇 Muted senders changed:', payload);
          // Recalculate count when muted senders change
          setTimeout(() => {
            fetchUnreadCount();
          }, 100);
        }
      )
      .subscribe();

    // Set up a periodic refresh as backup (every 30 seconds)
    const periodicRefresh = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    // Listen for custom events from Messages page for immediate updates
    const handleUnreadMessagesChanged = () => {
      fetchUnreadCount();
    };

    window.addEventListener('unreadMessagesChanged', handleUnreadMessagesChanged);

    // Cleanup subscription on unmount or business change
    return () => {
      subscription.unsubscribe();
      clearInterval(periodicRefresh);
      window.removeEventListener('unreadMessagesChanged', handleUnreadMessagesChanged);
    };
  }, [selectedBusiness?.id]);

  // Load app settings when business or apps change
  useEffect(() => {
    const loadAppSettings = async () => {
      if (!selectedBusiness?.id) {
        setAppSettingsLoaded(true);
        return;
      }

      try {
        console.log('🔍 FAB: Loading app settings for business:', selectedBusiness.id);
        
        // Load calendar app settings
        if (isCalendarAppInstalled) {
          const { data: calendarData, error: calendarError } = await supabase
            .rpc('get_app_setting', {
              p_business_id: selectedBusiness.id,
              p_app_slug: 'calendar',
              p_setting_key: 'show_location_booking_in_fab',
              p_default_value: true
            });

          if (!calendarError && calendarData !== null) {
            console.log('🔍 FAB: Calendar setting loaded:', calendarData);
            setShowLocationBookingInFAB(calendarData);
          } else {
            setShowLocationBookingInFAB(true); // Default to true if app is installed
          }
        } else {
          setShowLocationBookingInFAB(false); // Hide if app not installed
        }

        // Load mobile booking setting from calendar app (settings were moved there)
        if (isCalendarAppInstalled || isMobileCalendarAppInstalled) {
          const { data: mobileBookingData, error: mobileBookingError } = await supabase
            .rpc('get_app_setting', {
              p_business_id: selectedBusiness.id,
              p_app_slug: 'calendar',
              p_setting_key: 'show_mobile_booking_in_fab',
              p_default_value: true
            });

          if (!mobileBookingError && mobileBookingData !== null) {
            console.log('🔍 FAB: Mobile booking setting loaded:', mobileBookingData);
            setShowMobileBookingInFAB(mobileBookingData);
          } else {
            setShowMobileBookingInFAB(true); // Default to true if calendar app is installed
          }
        } else {
          setShowMobileBookingInFAB(false); // Hide if calendar app not installed
        }

        // Load tasks setting from calendar app
        if (isCalendarAppInstalled) {
          const { data: tasksData, error: tasksError } = await supabase
            .rpc('get_app_setting', {
              p_business_id: selectedBusiness.id,
              p_app_slug: 'calendar',
              p_setting_key: 'show_tasks_in_fab',
              p_default_value: false
            });

          if (!tasksError && tasksData !== null) {
            setShowTasksInFAB(tasksData);
          } else {
            setShowTasksInFAB(false);
          }

          const { data: fqFabData, error: fqFabError } = await supabase.rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_field_quotes_in_fab',
            p_default_value: false
          });

          if (!fqFabError && fqFabData !== null) {
            setShowFieldQuotesInFAB(!!fqFabData);
          } else {
            setShowFieldQuotesInFAB(false);
          }
        } else {
          setShowTasksInFAB(false);
          setShowFieldQuotesInFAB(false);
        }

        // Load CRM app settings (create opportunity from FAB — off by default)
        if (isCrmAppInstalled) {
          const { data: crmFabData, error: crmFabError } = await supabase
            .rpc('get_app_setting', {
              p_business_id: selectedBusiness.id,
              p_app_slug: 'crm',
              p_setting_key: 'create_opportunity_from_fab',
              p_default_value: false
            });

          if (!crmFabError && crmFabData !== null) {
            setShowOpportunityInFAB(!!crmFabData);
          } else {
            setShowOpportunityInFAB(false);
          }

          const { data: aiCardData, error: aiCardError } = await supabase
            .rpc('get_app_setting', {
              p_business_id: selectedBusiness.id,
              p_app_slug: 'crm',
              p_setting_key: 'ai_create_from_business_card',
              p_default_value: false
            });

          if (!aiCardError && aiCardData !== null) {
            setAiCardScanEnabled(!!aiCardData);
          } else {
            setAiCardScanEnabled(false);
          }
        } else {
          setShowOpportunityInFAB(false);
          setAiCardScanEnabled(false);
        }

        // Load marketing app settings (sales pin from FAB — off by default)
        if (isMarketingAppInstalled) {
          const { data: salesPinData, error: salesPinError } = await supabase
            .rpc('get_app_setting', {
              p_business_id: selectedBusiness.id,
              p_app_slug: 'marketing',
              p_setting_key: 'create_sales_pin_from_fab',
              p_default_value: false
            });

          if (!salesPinError && salesPinData !== null) {
            setShowSalesPinInFAB(!!salesPinData);
          } else {
            setShowSalesPinInFAB(false);
          }
        } else {
          setShowSalesPinInFAB(false);
        }

        // Load proposals app settings
        if (isProposalsAppInstalled) {
          const { data: proposalsData, error: proposalsError } = await supabase
            .rpc('get_app_setting', {
              p_business_id: selectedBusiness.id,
              p_app_slug: 'proposals',
              p_setting_key: 'show_proposal_in_fab',
              p_default_value: true
            });

          if (!proposalsError && proposalsData !== null) {
            console.log('🔍 FAB: Proposals setting loaded:', proposalsData);
            setShowProposalInFAB(proposalsData);
          } else {
            setShowProposalInFAB(true); // Default to true if app is installed
          }
        } else {
          setShowProposalInFAB(false); // Hide if app not installed
        }

        // Load projects app settings
        if (isProjectsAppInstalled) {
          const { data: projectsData, error: projectsError } = await supabase
            .rpc('get_app_setting', {
              p_business_id: selectedBusiness.id,
              p_app_slug: 'projects',
              p_setting_key: 'show_project_in_fab',
              p_default_value: true
            });

          if (!projectsError && projectsData !== null) {
            setShowProjectInFAB(projectsData);
          } else {
            setShowProjectInFAB(true); // Default to true if app is installed
          }
        } else {
          setShowProjectInFAB(false); // Hide if app not installed
        }

        // Load inventory app settings
        if (isInventoryAppInstalled) {
          const { data: inventoryData, error: inventoryError } = await supabase
            .rpc('get_app_setting', {
              p_business_id: selectedBusiness.id,
              p_app_slug: 'inventory',
              p_setting_key: 'show_withdraw_stock_in_fab',
              p_default_value: true
            });

          if (!inventoryError && inventoryData !== null) {
            console.log('🔍 FAB: Inventory setting loaded:', inventoryData);
            setShowWithdrawStockInFAB(inventoryData);
          } else {
            setShowWithdrawStockInFAB(true); // Default to true if app is installed
          }
        } else {
          setShowWithdrawStockInFAB(false); // Hide if app not installed
        }

        // Load payroll app settings
        if (isPayrollAppInstalled) {
          const { data: payrollData, error: payrollError } = await supabase
            .rpc('get_app_setting', {
              p_business_id: selectedBusiness.id,
              p_app_slug: 'payroll',
              p_setting_key: 'clock_in_from_fab',
              p_default_value: true
            });

          if (!payrollError && payrollData !== null) {
            console.log('🔍 FAB: Payroll clock-in setting loaded:', payrollData);
            setShowClockInFromFAB(payrollData);
          } else {
            setShowClockInFromFAB(true); // Default to true if app is installed
          }
        } else {
          setShowClockInFromFAB(false); // Hide if app not installed
        }

        // Load expenses app settings
        if (isExpensesAppInstalled) {
          const { data: expensesData, error: expensesError } = await supabase
            .rpc('get_app_setting', {
              p_business_id: selectedBusiness.id,
              p_app_slug: 'expenses',
              p_setting_key: 'create_expenses_from_fab',
              p_default_value: false
            });

          if (!expensesError && expensesData !== null) {
            console.log('🔍 FAB: Expenses setting loaded:', expensesData);
            setShowExpenseInFAB(expensesData);
          } else {
            setShowExpenseInFAB(false); // Default to false if app is installed
          }
        } else {
          setShowExpenseInFAB(false); // Hide if app not installed
        }

        // Load services app settings (subscriptions)
        if (isServicesAppInstalled) {
          const { data: servicesData, error: servicesError } = await supabase
            .rpc('get_app_setting', {
              p_business_id: selectedBusiness.id,
              p_app_slug: 'services',
              p_setting_key: 'enable_subscriptions',
              p_default_value: false
            });

          if (!servicesError && servicesData !== null) {
            setShowSubscriptionsInFAB(servicesData);
          } else {
            setShowSubscriptionsInFAB(false);
          }
        } else {
          setShowSubscriptionsInFAB(false);
        }

        setAppSettingsLoaded(true);
        console.log('🔍 FAB: All app settings loaded');
      } catch (error) {
        console.error('Error loading FAB app settings:', error);
        setAppSettingsLoaded(true);
      }
    };

    loadAppSettings();
  }, [selectedBusiness?.id, isCalendarAppInstalled, isMobileCalendarAppInstalled, isProposalsAppInstalled, isProjectsAppInstalled, isInventoryAppInstalled, isPayrollAppInstalled, isExpensesAppInstalled, isServicesAppInstalled, isCrmAppInstalled, isMarketingAppInstalled]);

  // Listen for app setting changes
  useEffect(() => {
    const handleCalendarSettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'show_location_booking_in_fab') {
        console.log('🔍 FAB: Calendar setting changed:', event.detail.value);
        setShowLocationBookingInFAB(event.detail.value);
      }
      if (event.detail.key === 'show_tasks_in_fab') {
        setShowTasksInFAB(event.detail.value);
      }
      if (event.detail.key === 'show_field_quotes_in_fab') {
        setShowFieldQuotesInFAB(!!event.detail.value);
      }
    };

    const handleMobileCalendarSettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'show_mobile_booking_in_fab') {
        console.log('🔍 FAB: Mobile calendar setting changed:', event.detail.value);
        setShowMobileBookingInFAB(event.detail.value);
      }
    };

    const handleProposalsSettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'show_proposal_in_fab') {
        console.log('🔍 FAB: Proposals setting changed:', event.detail.value);
        setShowProposalInFAB(event.detail.value);
      }
    };

    const handleProjectsSettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'show_project_in_fab') {
        setShowProjectInFAB(event.detail.value);
      }
    };

    const handleInventorySettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'show_withdraw_stock_in_fab') {
        console.log('🔍 FAB: Inventory setting changed:', event.detail.value);
        setShowWithdrawStockInFAB(event.detail.value);
      }
    };

    const handlePayrollSettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'clock_in_from_fab') {
        console.log('🔍 FAB: Payroll clock-in setting changed:', event.detail.value);
        setShowClockInFromFAB(event.detail.value);
      }
    };

    const handleExpensesSettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'create_expenses_from_fab') {
        console.log('🔍 FAB: Expenses setting changed:', event.detail.value);
        setShowExpenseInFAB(event.detail.value);
      }
    };

    const handleServicesSettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'enable_subscriptions') {
        setShowSubscriptionsInFAB(event.detail.value);
      }
    };

    const handleCrmSettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'create_opportunity_from_fab') {
        setShowOpportunityInFAB(!!event.detail.value);
      }
      if (event.detail.key === 'ai_create_from_business_card') {
        setAiCardScanEnabled(!!event.detail.value);
      }
    };

    const handleMarketingSettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'create_sales_pin_from_fab') {
        setShowSalesPinInFAB(!!event.detail.value);
      }
    };

    window.addEventListener('calendarSettingChanged', handleCalendarSettingChange as EventListener);
    window.addEventListener('mobileCalendarSettingChanged', handleMobileCalendarSettingChange as EventListener);
    window.addEventListener('proposalsSettingChanged', handleProposalsSettingChange as EventListener);
    window.addEventListener('projectsSettingChanged', handleProjectsSettingChange as EventListener);
    window.addEventListener('inventorySettingChanged', handleInventorySettingChange as EventListener);
    window.addEventListener('payrollSettingChanged', handlePayrollSettingChange as EventListener);
    window.addEventListener('expensesSettingChanged', handleExpensesSettingChange as EventListener);
    window.addEventListener('servicesSettingChanged', handleServicesSettingChange as EventListener);
    window.addEventListener('crmSettingChanged', handleCrmSettingChange as EventListener);
    window.addEventListener('marketingSettingChanged', handleMarketingSettingChange as EventListener);

    return () => {
      window.removeEventListener('calendarSettingChanged', handleCalendarSettingChange as EventListener);
      window.removeEventListener('mobileCalendarSettingChanged', handleMobileCalendarSettingChange as EventListener);
      window.removeEventListener('proposalsSettingChanged', handleProposalsSettingChange as EventListener);
      window.removeEventListener('projectsSettingChanged', handleProjectsSettingChange as EventListener);
      window.removeEventListener('inventorySettingChanged', handleInventorySettingChange as EventListener);
      window.removeEventListener('payrollSettingChanged', handlePayrollSettingChange as EventListener);
      window.removeEventListener('expensesSettingChanged', handleExpensesSettingChange as EventListener);
      window.removeEventListener('servicesSettingChanged', handleServicesSettingChange as EventListener);
      window.removeEventListener('crmSettingChanged', handleCrmSettingChange as EventListener);
      window.removeEventListener('marketingSettingChanged', handleMarketingSettingChange as EventListener);
    };
  }, []);

  // Run mandatory document procedures before a clock action; only proceed once all are submitted.
  const gateProcedures = async (event: ProcedureTriggerEvent, proceed: () => void | Promise<void>) => {
    try {
      if (!selectedBusiness?.id) { await proceed(); return; }
      const applicable = await getClockProcedures(selectedBusiness.id, event);
      const docs = applicable
        .filter((p) => p.mandatory && p.template_id)
        .map((p) => p.template_id);
      const unique = [...new Set(docs)];
      if (unique.length === 0) { await proceed(); return; }
      setShowBookingOptions(false);
      procedureProceedRef.current = proceed;
      procedureCompletingRef.current = false;
      setProcedureQueue(unique);
      setActiveProcedureTemplateId(unique[0]);
    } catch (e) {
      console.error('Procedure gate error:', e);
      await proceed(); // fail-open so staff are never locked out by a check failure
    }
  };

  const handleProcedureCompleted = () => {
    procedureCompletingRef.current = true;
    setProcedureQueue((prev) => {
      const rest = prev.slice(1);
      if (rest.length > 0) {
        setActiveProcedureTemplateId(rest[0]);
      } else {
        setActiveProcedureTemplateId(null);
        const proceed = procedureProceedRef.current;
        procedureProceedRef.current = null;
        if (proceed) Promise.resolve(proceed()).catch((e) => console.error('Clock action error:', e));
      }
      return rest;
    });
  };

  const handleProcedureClose = () => {
    // Closing right after a successful submit is part of the advance flow, not a cancel.
    if (procedureCompletingRef.current) { procedureCompletingRef.current = false; return; }
    setActiveProcedureTemplateId(null);
    setProcedureQueue([]);
    procedureProceedRef.current = null;
    toast.error('This document is required to continue.');
  };

  const handleClockIn = async () => {
    if (!selectedBusiness?.id || !user?.id || !resourceId || isClockBusy) return;
    setIsClockBusy(true);
    try {
      const result = await clockIn(selectedBusiness.id, { source: 'fab' });
      applyTimeclockEntry(result.entry);
      setShowBookingOptions(false);
      if (result.already_open) {
        toast(`You've been clocked in since ${new Date(result.entry.clock_in).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.`);
      }
    } catch (error) {
      console.error('Error clocking in:', error);
      toast.error(error instanceof TimeclockError ? error.message : 'Failed to clock in. Please try again.');
    } finally {
      setIsClockBusy(false);
    }
  };

  const performClockOut = async () => {
    if (!selectedBusiness?.id || !currentPayrollEntryId || !resourceId) return;
    await clockOut(selectedBusiness.id, { source: 'fab', stopJobTimers: true });
    applyTimeclockEntry(null);
    setShowBookingOptions(false);
  };

  const loadActiveJobTimers = async (): Promise<{ booking_id: string }[]> => {
    if (!selectedBusiness?.id) return [];
    const status = await getTimeclockStatus(selectedBusiness.id);
    return status.active_job_timers.map((t) => ({ booking_id: t.booking_id }));
  };

  const handleClockOut = async () => {
    if (!currentPayrollEntryId || !resourceId || !startTime || isClockBusy) return;
    setIsClockBusy(true);
    try {
      const entries = await loadActiveJobTimers();
      if (entries.length > 0) {
        setFabActiveJobTimerEntries(entries);
        setFabPendingAction('clock_out');
        setFabActiveJobTimerModalOpen(true);
        setShowBookingOptions(false);
        setIsClockBusy(false);
        return;
      }
      await performClockOut();
    } catch (error) {
      console.error('Error clocking out:', error);
      toast.error(error instanceof TimeclockError ? error.message : 'Failed to clock out. Please try again.');
    } finally {
      setIsClockBusy(false);
    }
  };

  const performBreak = async () => {
    if (!selectedBusiness?.id || !currentPayrollEntryId || !resourceId) return;
    const result = onBreak
      ? await endBreak(selectedBusiness.id)
      : await startBreak(selectedBusiness.id);
    applyTimeclockEntry(result.entry);
    setShowBookingOptions(false);
  };

  const handleBreak = async () => {
    if (!currentPayrollEntryId || !resourceId) return;
    try {
      const entries = await loadActiveJobTimers();
      if (entries.length > 0) {
        setFabActiveJobTimerEntries(entries);
        setFabPendingAction('break');
        setFabActiveJobTimerModalOpen(true);
        setShowBookingOptions(false);
        return;
      }
      await performBreak();
    } catch (error) {
      console.error('Error handling break:', error);
      toast.error(error instanceof TimeclockError ? error.message : 'Failed to update break status. Please try again.');
    }
  };

  const handleStopTimersAndProceedFAB = async () => {
    if (!resourceId || !supabase || fabPendingAction == null) return;
    setFabActionModalLoading(true);
    try {
      // Clock-out stops running job timers inside the engine; a break stops them here first.
      if (fabPendingAction === 'break') {
        for (const { booking_id } of fabActiveJobTimerEntries) {
          await supabase.rpc('stop_job_timer', {
            p_booking_id: booking_id,
            p_resource_id: resourceId,
            p_method: 'manual',
          });
        }
      }
      setFabActiveJobTimerModalOpen(false);
      setFabActiveJobTimerEntries([]);
      if (fabPendingAction === 'clock_out') await performClockOut();
      else await performBreak();
      setFabPendingAction(null);
    } catch (error) {
      console.error('Error stopping timers before FAB action:', error);
      toast.error(error instanceof TimeclockError ? error.message : 'Failed to stop timers. Please try again.');
    } finally {
      setFabActionModalLoading(false);
    }
  };

  // Close FAB create menu on outside click — don't also trigger calendar slot create
  const closeBookingOptions = useCallback(() => setShowBookingOptions(false), []);
  useDismissOverlayProtectingCalendarCreate(showBookingOptions, closeBookingOptions, [fabRef]);

  // Centre AI circle. While the voice fan is parked, a tap opens Agents.
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const closeAiMenu = useCallback(() => setAiMenuOpen(false), []);
  useDismissOverlayProtectingCalendarCreate(aiMenuOpen, closeAiMenu, [fabRef]);
  useEffect(() => { setAiMenuOpen(false); }, [location.pathname]);
  const agentsEnabled = selectedBusiness?.agents_enabled !== false;
  // Green while any bot is working. Red wins if any bot is waiting on the owner.
  const [agentMark, setAgentMark] = useState<'working' | 'blocked' | null>(null);
  const [expectWork, setExpectWork] = useState(false);
  const expectAt = useRef(0);
  useEffect(() => {
    const on = () => { expectAt.current = Date.now(); setExpectWork(true); };
    const off = () => setExpectWork(false);
    window.addEventListener('agents:expect-work', on);
    window.addEventListener('agents:expect-work-clear', off);
    return () => {
      window.removeEventListener('agents:expect-work', on);
      window.removeEventListener('agents:expect-work-clear', off);
    };
  }, []);
  useEffect(() => {
    const businessId = selectedBusiness?.id;
    if (!businessId || !aiVoiceAllowed) {
      setAgentMark(null);
      return;
    }
    let active = true;
    const live = new Map<string, { status: string; chatId?: string; updatedAt?: string }>();
    const LIVE = ['thinking', 'working', 'waiting', 'blocked'];
    const publish = () => {
      const seen = readStatusSeen(businessId);
      const statuses = [...live.values()].filter((row) => {
        if (row.status === 'thinking' || row.status === 'working') return true;
        if (row.status !== 'waiting' && row.status !== 'blocked') return false;
        const seenAt = row.chatId ? seen[row.chatId] : undefined;
        const updated = row.updatedAt ? Date.parse(row.updatedAt) : NaN;
        const seenMs = seenAt ? Date.parse(seenAt) : NaN;
        if (Number.isFinite(seenMs) && Number.isFinite(updated) && updated <= seenMs) return false;
        return true;
      }).map((row) => row.status);
      const mark = statuses.includes('blocked') || statuses.includes('waiting')
        ? 'blocked'
        : statuses.some((status) => status === 'thinking' || status === 'working')
          ? 'working'
          : null;
      setAgentMark(mark);
      // The optimistic dot is only for the gap before the first status row.
      // Once the database says nobody is working, the dot has to go.
      if (mark !== 'working' && Date.now() - expectAt.current > 8000) setExpectWork(false);
    };
    const apply = (row: { id?: string; status?: string; chat_id?: string; updated_at?: string }, removed = false) => {
      if (!row.id) return;
      const prev = live.get(row.id);
      const prevTime = prev?.updatedAt ? Date.parse(prev.updatedAt) : 0;
      const nextTime = row.updated_at ? Date.parse(row.updated_at) : 0;
      if (prev && Number.isFinite(prevTime) && Number.isFinite(nextTime) && nextTime < prevTime) return;
      if (removed || !row.status || !LIVE.includes(row.status)) live.delete(row.id);
      else live.set(row.id, { status: row.status, chatId: row.chat_id, updatedAt: row.updated_at });
      publish();
    };
    const pull = () => {
      void supabase
        .from('agent_bot_sessions')
        .select('id, status, chat_id, updated_at')
        .eq('business_id', businessId)
        .in('status', LIVE)
        .then(({ data }) => {
          if (!active) return;
          live.clear();
          for (const row of data || []) {
            const item = row as { id: string; status: string; chat_id: string; updated_at: string };
            if (item.id && LIVE.includes(item.status)) {
              live.set(item.id, { status: item.status, chatId: item.chat_id, updatedAt: item.updated_at });
            }
          }
          publish();
        });
    };
    const onSeen = () => { if (active) publish(); };
    const onVisible = () => { if (document.visibilityState === 'visible') pull(); };
    window.addEventListener('agents:status-seen', onSeen);
    document.addEventListener('visibilitychange', onVisible);
    pull();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') pull();
    }, 4000);
    const channel = supabase.channel(`fab-agent-sessions-${businessId}-${crypto.randomUUID()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'agent_bot_sessions', filter: `business_id=eq.${businessId}` }, (payload) => {
        if (payload.eventType === 'DELETE') apply(payload.old as { id?: string }, true);
        else apply(payload.new as { id?: string; status?: string; chat_id?: string; updated_at?: string });
      })
      .subscribe((status) => { if (status === 'SUBSCRIBED') pull(); });
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('agents:status-seen', onSeen);
      document.removeEventListener('visibilitychange', onVisible);
      void supabase.removeChannel(channel);
    };
  }, [selectedBusiness?.id, aiVoiceAllowed]);
  const visibleAgentMark = agentMark ?? (expectWork ? 'working' : null);
  useEffect(() => {
    if (agentMark) setExpectWork(false);
  }, [agentMark]);
  const agentMarkText = visibleAgentMark === 'blocked' ? 'A bot is waiting for you' : visibleAgentMark === 'working' ? 'Bots are working' : null;
  const agentMarkOnButton = !!visibleAgentMark && (!aiMenuOpen || !agentsEnabled);
  const [agentNotice, setAgentNotice] = useState<AgentNotice | null>(null);
  const viewingChatRef = useRef<string | null>(null);
  const dismissedNoticeIdsRef = useRef<Set<string>>(new Set());
  const noticeBusinessIdRef = useRef<string | null>(selectedBusiness?.id ?? null);
  noticeBusinessIdRef.current = selectedBusiness?.id ?? null;
  const persistDismissedNotice = useCallback((id: string) => {
    dismissedNoticeIdsRef.current.add(id);
    const businessId = noticeBusinessIdRef.current;
    if (!businessId) return;
    const ids = loadDismissedNoticeIds(businessId);
    ids.add(id);
    saveDismissedNoticeIds(businessId, ids);
    dismissedNoticeIdsRef.current = ids;
  }, []);
  const markChatSeen = useCallback((chatId: string, through?: string) => {
    const businessId = noticeBusinessIdRef.current;
    if (!businessId || !chatId) return;
    const seen = readStatusSeen(businessId);
    const prev = seen[chatId] ? Date.parse(seen[chatId]) : 0;
    const throughMs = through ? Date.parse(through) : 0;
    const stamp = Math.max(Date.now(), Number.isFinite(throughMs) ? throughMs : 0);
    if (Number.isFinite(prev) && prev >= stamp) return;
    writeStatusSeen(businessId, { ...seen, [chatId]: new Date(stamp).toISOString() });
  }, []);
  // Entering the bots page hides the bubble before the chat is opened, so keep the notice
  // until that chat is actually opened and the dismiss can be recorded.
  const hiddenNoticeRef = useRef<AgentNotice | null>(null);
  const onAgentsPageRef = useRef(false);
  onAgentsPageRef.current = onAgentsPage;
  const showAgentNotice = useCallback((row: AgentNotice | null) => {
    if (onAgentsPageRef.current) return;
    if (!row?.id || !row.chat_id) return;
    const businessId = noticeBusinessIdRef.current;
    if (dismissedNoticeIdsRef.current.has(row.id)) return;
    if (noticeHandled(businessId, row)) return;
    if (row.chat_id === viewingChatRef.current) return;
    setAgentNotice(row);
  }, []);
  useEffect(() => {
    const onView = (event: Event) => {
      const detail = (event as CustomEvent<{ chatId?: string | null; explicit?: boolean }>).detail;
      const chatId = detail?.chatId || null;
      viewingChatRef.current = chatId;
      if (!detail?.explicit || !chatId) return;
      const hidden = hiddenNoticeRef.current;
      if (hidden?.chat_id === chatId) {
        persistDismissedNotice(hidden.id);
        markChatSeen(chatId, hidden.created_at);
        hiddenNoticeRef.current = null;
      } else {
        markChatSeen(chatId);
      }
      setAgentNotice((current) => {
        if (!current || current.chat_id !== chatId) return current;
        persistDismissedNotice(current.id);
        markChatSeen(chatId, current.created_at);
        return null;
      });
    };
    window.addEventListener('agents:viewing-chat', onView);
    const onSeen = () => {
      const businessId = noticeBusinessIdRef.current;
      setAgentNotice((current) => (current && noticeHandled(businessId, current) ? null : current));
    };
    window.addEventListener('agents:status-seen', onSeen);
    return () => {
      window.removeEventListener('agents:viewing-chat', onView);
      window.removeEventListener('agents:status-seen', onSeen);
    };
  }, [persistDismissedNotice, markChatSeen]);
  useEffect(() => {
    if (!onAgentsPage) return;
    setAgentNotice((current) => {
      if (current) hiddenNoticeRef.current = current;
      return null;
    });
  }, [onAgentsPage]);
  useEffect(() => {
    const businessId = selectedBusiness?.id;
    if (!businessId || !aiVoiceAllowed || !supabase) {
      setAgentNotice(null);
      return;
    }
    let active = true;
    const accept = (row: AgentNotice) => {
      if (!active || document.visibilityState !== 'visible') return;
      showAgentNotice(row);
    };
    const onSw = (event: MessageEvent) => {
      if (event.data?.type !== 'AGENT_NOTICE') return;
      const notice = event.data.notice as AgentNotice | undefined;
      if (notice) accept(notice);
    };
    navigator.serviceWorker?.addEventListener('message', onSw);
    const channel = supabase.channel(`agent-notices-${businessId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'agent_notices', filter: `business_id=eq.${businessId}` }, (payload) => {
        accept(payload.new as AgentNotice);
      })
      .subscribe();
    const pullLatest = () => {
      if (document.visibilityState !== 'visible') return;
      void supabase.from('agent_notices').select('id, chat_id, kind, title, body, created_at').eq('business_id', businessId).order('created_at', { ascending: false }).limit(1).maybeSingle().then(({ data }) => {
        if (!active || !data) return;
        const created = new Date(String((data as { created_at?: string }).created_at || 0)).getTime();
        if (!created || Date.now() - created > 15 * 60 * 1000) return;
        showAgentNotice(data as AgentNotice);
      });
    };
    pullLatest();
    document.addEventListener('visibilitychange', pullLatest);
    return () => {
      active = false;
      navigator.serviceWorker?.removeEventListener('message', onSw);
      document.removeEventListener('visibilitychange', pullLatest);
      void supabase.removeChannel(channel);
    };
  }, [selectedBusiness?.id, aiVoiceAllowed, showAgentNotice]);
  const dismissAgentNotice = () => {
    if (!agentNotice) return;
    persistDismissedNotice(agentNotice.id);
    markChatSeen(agentNotice.chat_id, agentNotice.created_at);
    hiddenNoticeRef.current = null;
    setAgentNotice(null);
  };
  // Nexus's home conversation, for the press-and-hold call. Resolved once per
  // business, the first time a finger lands on the AI button.
  const nexusHomeRef = useRef<{ businessId: string; promise: Promise<{ botId: string; chatId: string } | null> } | null>(null);
  const nexusHome = useCallback(() => {
    const businessId = selectedBusiness?.id;
    if (!businessId) return Promise.resolve(null);
    if (nexusHomeRef.current?.businessId !== businessId) {
      nexusHomeRef.current = {
        businessId,
        promise: hub<{ botId: string; chatId: string }>({ action: 'home', businessId })
          .then((home) => ({ botId: home.botId, chatId: home.chatId }))
          .catch(() => { nexusHomeRef.current = null; return null; }),
      };
    }
    return nexusHomeRef.current.promise;
  }, [selectedBusiness?.id]);
  // The call a tap on the AI button should open: the live bot conversation, if any.
  const chatVoiceLive = voiceSurface === 'chat' && (voiceStatus === 'active' || voiceStatus === 'connecting');
  const openLiveChat = () => {
    if (!chatVoiceLive) return false;
    openAgents(voiceChatTarget?.chatId ? `/agents?chat=${voiceChatTarget.chatId}` : '/agents');
    return true;
  };
  // Holding the AI button starts a voice call with Nexus right here: the same
  // in-chat call the Nexus conversation's voice button starts, shown as a pill
  // over whatever screen is open. Nothing navigates; the owner carries on.
  // Already on the bots screen, a hold does nothing — voice starts from the chat.
  const holdToTalk = useHoldAction(() => {
    if (onAgentsPage) return;
    void hapticMedium();
    setShowBookingOptions(false);
    setAiMenuOpen(false);
    if (openLiveChat()) return;
    // Voice is admin-only; for anyone else the hold just opens Agents.
    if (!aiVoiceAllowed) { openAgents(); return; }
    void nexusHome().then((home) => {
      if (!home) { openAgents(); return; }
      window.dispatchEvent(new CustomEvent('assistant:startVoice', { detail: { surface: 'chat', botId: home.botId, chatId: home.chatId } }));
    });
  });
  // A finger on the button gives the Nexus call a head start, the way the chat's own button gets one.
  const headStartNexusCall = () => {
    if (onAgentsPage || chatVoiceLive || !aiVoiceAllowed || !selectedBusiness?.id || needsAiOnboarding) return;
    void nexusHome().then((home) => {
      if (!home || !selectedBusiness?.id) return;
      const installedAppNames = [...(coreApps || []), ...(installedAddons || [])]
        .map((a) => a.app?.name)
        .filter((name): name is string => Boolean(name));
      prewarmVoice(installedAppNames, selectedBusiness.id, { botId: home.botId, chatId: home.chatId });
    });
  };
  const openAgentNotice = () => {
    if (!agentNotice) return;
    persistDismissedNotice(agentNotice.id);
    markChatSeen(agentNotice.chat_id, agentNotice.created_at);
    hiddenNoticeRef.current = null;
    const chatId = agentNotice.chat_id;
    setAgentNotice(null);
    openAgents(`/agents?chat=${chatId}`);
  };

  // AI assistant's direct entry: open a booking form immediately, optionally
  // with a customer preselected (found via the assistant's lookup_customer tool).
  const [assistantInitialCustomer, setAssistantInitialCustomer] = useState<Customer | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      setAssistantInitialCustomer(detail.customer || null);
      setShowBookingOptions(false);
      if (detail.type === 'mobile') setIsCreateMobileBookingModalOpen(true);
      else setIsCreateBookingModalOpen(true);
    };
    window.addEventListener('assistant:createBooking', handler);
    return () => window.removeEventListener('assistant:createBooking', handler);
  }, []);

  // Convert an existing proposal: the assistant's convert_proposal_to_booking
  // tool opens the booking form prefilled from the proposal — the exact same
  // CreateBookingModal initialProposal path the Proposal Details Convert
  // button uses (services, prices, deposits, and payments carry over).
  const [assistantInitialProposal, setAssistantInitialProposal] = useState<unknown>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      if (!detail.proposal) return;
      setAssistantInitialCustomer(null);
      setAssistantInitialProposal(detail.proposal);
      setShowBookingOptions(false);
      setIsCreateBookingModalOpen(true);
    };
    window.addEventListener('assistant:convertProposal', handler);
    return () => window.removeEventListener('assistant:convertProposal', handler);
  }, []);

  // Same for events: the assistant's start_task tool opens the Create Event form
  // (optionally with a customer found via lookup_customer).
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      setAssistantInitialCustomer(detail.customer || null);
      setShowBookingOptions(false);
      setIsCreateTaskModalOpen(true);
    };
    window.addEventListener('assistant:createTask', handler);
    return () => window.removeEventListener('assistant:createTask', handler);
  }, []);

  // Same for proposals: the assistant's start_proposal tool opens the New
  // Proposal form with the looked-up customer preselected.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      setAssistantInitialCustomer(detail.customer || null);
      setShowBookingOptions(false);
      setIsCreateProposalModalOpen(true);
    };
    window.addEventListener('assistant:createProposal', handler);
    return () => window.removeEventListener('assistant:createProposal', handler);
  }, []);

  const handleSuccess = async () => {
    if (onSuccess) {
      await onSuccess();
    }
    setShowBookingOptions(false);
    
    // Dispatch custom event to refresh mobile calendar
    window.dispatchEvent(new CustomEvent('mobileBookingCreated'));
  };




  // Horizontal navigation handlers - REMOVED


  // Long press handlers for edit mode
  // Long press and edit mode functions - REMOVED (were only used for horizontal nav)

  useEffect(() => {
    if (selectedBusiness?.id) {
      const fetchResources = async () => {
        try {
          await getResources(selectedBusiness.id);
          // Resources fetching - REMOVED (not used anymore)
        } catch (error) {
          console.error('Error fetching resources:', error);
        }
      };
      fetchResources();
    }
  }, [selectedBusiness?.id]);

  // Navigation items functions - REMOVED (were only used for horizontal nav)

  // Load horizontal navigation config - REMOVED

  // Cleanup - REMOVED (was only for long press timer)

  /**
   * The apps menu and the create menu are both anchored to the bar, so only one
   * may be open at a time — and tapping any other tab dismisses whichever is up.
   */
  const closeBarMenus = () => {
    setShowBookingOptions(false);
    setAiMenuOpen(false);
    onSetSidebarOpen?.(false);
  };

  const voiceOrb = (
    <VoiceOrb
      size={34}
      pulse={voiceOn && (voiceStatus === 'connecting' || voiceProcessing || voiceSpeaking)}
    />
  );

  return (
    <div>
      <div
        className={`fixed ${
          bottomBarActive
            ? 'bottom-0 left-0 right-0 border-t border-gray-200 bg-white/95 backdrop-blur-xl'
            : 'bottom-6 right-6'
        } ${
          isNexusOverlayOpen
            ? 'z-[60]'
            : bottomBarActive
              // Above the apps menu (z-90) so the protruding centre circle and
              // the tabs stay visible and tappable while the menu is open.
              ? 'z-[95]'
              : 'z-50'
        } ${fabClusterHidden ? 'hidden' : 'block'}`}
        style={
          bottomBarActive
            ? {
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                WebkitBackdropFilter: 'blur(20px)',
              }
            : undefined
        }
      >
        <div
          ref={fabRef}
          className={bottomBarActive ? 'relative flex items-stretch' : undefined}
          style={bottomBarActive ? { height: `${MOBILE_BOTTOM_BAR_HEIGHT_REM}rem` } : undefined}
        >

          {bottomBarActive ? (
            <>
              {/* Equal side groups keep the centre circle in the middle when a
                  tab is hidden. Admins get Control Centre on the left because
                  AI owns the centre. Regular resources leave that slot empty
                  and keep Control Centre in the middle. */}
              <div className="flex min-w-0 flex-1 items-stretch">
                {aiVoiceAllowed ? (
                  <BarTab
                    icon={<LayoutDashboard className="h-5 w-5" />}
                    label="Control Centre"
                    onClick={() => {
                      closeBarMenus();
                      navigate('/admin-dashboard');
                    }}
                    active={location.pathname === '/admin-dashboard'}
                  />
                ) : null}
                {showMessagesTab ? (
                  <BarTab
                    icon={<MessageSquare className="h-5 w-5" />}
                    label="Messages"
                    onClick={() => {
                      closeBarMenus();
                      navigate('/messages');
                    }}
                    badge={unreadMessagesCount + unviewedMissedCallsCount}
                    active={
                      location.pathname === '/messages' ||
                      location.pathname === '/calls-beta'
                    }
                  />
                ) : null}
              </div>

              {/* Centre block. Admins get the protruding AI circle. A regular
                  resource gets the same Control Centre tab as the side buttons,
                  kept in this slot so it stays in the middle. */}
              <div
                className={`relative flex shrink-0 items-stretch justify-center ${
                  aiVoiceAllowed ? 'w-[4.5rem]' : 'w-[5.25rem]'
                }`}
              >
                {agentNotice && !onAgentsPage ? (
                  <AgentSpeechBubble
                    notice={agentNotice}
                    onOpen={openAgentNotice}
                    onDismiss={dismissAgentNotice}
                    className="absolute bottom-[calc(100%+1.75rem)] left-1/2 z-20 w-[220px] -translate-x-1/2"
                  />
                ) : null}
                {aiVoiceAllowed ? (
                  AI_VOICE_FAN_ENABLED ? (
                  <>
                    <AiFanOption
                      open={aiMenuOpen}
                      x={-70}
                      y={-74}
                      delayMs={0}
                      icon={voiceOn ? <X className="h-5 w-5" /> : voiceOrb}
                      label={voiceOn ? 'End voice' : 'Voice mode'}
                      ariaLabel={voiceOn ? 'End the conversation with Nexus' : 'Talk to Nexus'}
                      anchor="voice-mode-button"
                      className={
                        voiceOn
                          ? 'bg-gradient-to-br from-rose-500 to-red-600'
                          : 'bg-gradient-to-br from-gray-800 to-black'
                      }
                      shadow={
                        voiceOn
                          ? '0 8px 24px rgba(225, 29, 72, 0.4)'
                          : '0 8px 24px rgba(17, 24, 39, 0.35)'
                      }
                      onClick={() => {
                        setAiMenuOpen(false);
                        void hapticLight();
                        handleVoiceToggle();
                      }}
                    />
                    {agentsEnabled ? (
                      <AiFanOption
                        open={aiMenuOpen}
                        x={70}
                        y={-74}
                        delayMs={50}
                        icon={<NexusFace size={34} />}
                        label="Agents"
                        ariaLabel={agentMarkText ? `Open Agents. ${agentMarkText}` : 'Open Agents'}
                        mark={aiMenuOpen && visibleAgentMark ? <AgentWorkMark signal={visibleAgentMark} /> : null}
                        className="bg-gradient-to-br from-gray-800 to-black"
                        shadow="0 8px 24px rgba(17, 24, 39, 0.35)"
                        onClick={() => {
                          setAiMenuOpen(false);
                          void hapticLight();
                          dismissAgentNotice();
                          openAgents();
                        }}
                      />
                    ) : null}
                    <button
                      type="button"
                      onClick={() => {
                        setShowBookingOptions(false);
                        onSetSidebarOpen?.(false);
                        void hapticLight();
                        // While a voice session is running, this button ends it.
                        // The fan only opens once voice is off.
                        if (voiceOn) {
                          setAiMenuOpen(false);
                          handleVoiceToggle();
                          return;
                        }
                        setAiMenuOpen((open) => !open);
                      }}
                      onPointerDown={() => { if (!voiceOn) prewarmVoiceSession(); }}
                      aria-label={`${voiceOn ? 'End the conversation with Nexus' : aiMenuOpen ? 'Close AI options' : 'Nexus AI'}${agentMarkOnButton && agentMarkText ? `. ${agentMarkText}` : ''}`}
                      aria-expanded={voiceOn ? false : aiMenuOpen}
                      aria-haspopup={voiceOn ? undefined : 'menu'}
                      className={`absolute -top-5 flex h-14 w-14 items-center justify-center rounded-full text-white ring-4 ring-white touch-manipulation ${
                        voiceOn
                          ? 'bg-gradient-to-br from-rose-500 to-red-600'
                          : 'bg-gradient-to-br from-gray-800 to-black'
                      }`}
                      style={{
                        boxShadow: voiceOn
                          ? '0 8px 24px rgba(225, 29, 72, 0.4)'
                          : '0 8px 24px rgba(17, 24, 39, 0.35)',
                        WebkitTapHighlightColor: 'transparent',
                        transform: aiMenuOpen ? 'scale(0.92)' : 'scale(1)',
                        transition: `transform 380ms ${FAN_SPRING}, background-color 300ms ease`,
                      }}
                    >
                      <span className="relative flex h-9 w-9 items-center justify-center">
                        <span
                          className="absolute inset-0 flex items-center justify-center"
                          style={{
                            opacity: aiMenuOpen ? 0 : 1,
                            transform: aiMenuOpen ? 'rotate(90deg) scale(0.5)' : 'rotate(0deg) scale(1)',
                            transition: `transform 380ms ${FAN_SPRING}, opacity 160ms ease`,
                          }}
                        >
                          <NexusFace
                            size={36}
                            pulse={voiceOn && (voiceStatus === 'connecting' || voiceProcessing)}
                          />
                        </span>
                        <span
                          className="absolute inset-0 flex items-center justify-center"
                          style={{
                            opacity: aiMenuOpen ? 1 : 0,
                            transform: aiMenuOpen ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.5)',
                            transition: `transform 380ms ${FAN_SPRING}, opacity 160ms ease`,
                          }}
                        >
                          <X className="h-6 w-6" />
                        </span>
                      </span>
                      {agentMarkOnButton && visibleAgentMark ? (
                        <AgentWorkMark signal={visibleAgentMark} />
                      ) : voiceStatus === 'active' && !aiMenuOpen ? (
                        <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60" />
                          <span className="relative inline-flex h-3 w-3 rounded-full border-2 border-red-500 bg-white" />
                        </span>
                      ) : null}
                    </button>
                  </>
                  ) : (
                    <button
                      type="button"
                      {...holdToTalk.handlers}
                      onPointerDown={(e) => {
                        if (onAgentsPage) return;
                        holdToTalk.handlers.onPointerDown(e);
                        headStartNexusCall();
                      }}
                      onClick={() => {
                        if (holdToTalk.consumeHold()) return;
                        closeBarMenus();
                        void hapticLight();
                        dismissAgentNotice();
                        if (openLiveChat()) return;
                        openAgents();
                      }}
                      aria-label={onAgentsPage
                        ? (agentMarkText ? `Agents. ${agentMarkText}` : 'Agents')
                        : (agentMarkText ? `Open Agents. ${agentMarkText}. Press and hold to talk to Nexus` : 'Open Agents. Press and hold to talk to Nexus')}
                      className="absolute -top-5 flex h-14 w-14 select-none items-center justify-center rounded-full bg-gradient-to-br from-gray-800 to-black text-white ring-4 ring-white touch-manipulation active:scale-95"
                      style={{
                        boxShadow: '0 8px 24px rgba(17, 24, 39, 0.35)',
                        WebkitTapHighlightColor: 'transparent',
                        WebkitTouchCallout: 'none',
                      }}
                    >
                      <NexusFace size={36} />
                      {agentMarkOnButton && visibleAgentMark ? (
                        <AgentWorkMark signal={visibleAgentMark} />
                      ) : null}
                    </button>
                  )
                ) : (
                  <BarTab
                    icon={<LayoutDashboard className="h-5 w-5" />}
                    label="Control Centre"
                    onClick={() => {
                      closeBarMenus();
                      navigate('/admin-dashboard');
                    }}
                    active={location.pathname === '/admin-dashboard'}
                  />
                )}
              </div>

              <div className="flex min-w-0 flex-1 items-stretch">
                <BarTab
                  icon={
                    <Plus
                      className={`h-5 w-5 transition-transform duration-300 ${
                        showBookingOptions ? 'rotate-45' : ''
                      }`}
                    />
                  }
                  label="Create"
                  onClick={() => {
                    const opening = !showBookingOptions;
                    if (opening) {
                      onSetSidebarOpen?.(false);
                      setAiMenuOpen(false);
                    }
                    setShowBookingOptions(opening);
                  }}
                  disabled={permissionLevel === 'view'}
                  active={showBookingOptions}
                  ariaLabel="Create new"
                  anchor="blue-plus"
                />

                <BarTab
                  icon={<Menu className="h-5 w-5" />}
                  label="Apps"
                  onClick={() => {
                    setShowBookingOptions(false);
                    setAiMenuOpen(false);
                    onSetSidebarOpen?.(!sidebarOpen);
                  }}
                  active={sidebarOpen}
                  ariaLabel="Open apps menu"
                  ariaPressed={sidebarOpen}
                  anchor="menu-button"
                />
              </div>
            </>
          ) : (
            <>
          <div className="relative">
            <button 
              disabled={permissionLevel === 'view'}
              onClick={() => {
                setAiMenuOpen(false);
                setShowBookingOptions(!showBookingOptions);
              }}
              className={`fab-button backdrop-blur-xl bg-gradient-to-br from-blue-500 to-blue-700 border border-white/20 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 p-3 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
                showBookingOptions ? 'from-blue-600 to-blue-800 scale-105' : ''
              }`}
              style={{
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)'
              }}
              data-assistant-anchor="blue-plus"
              aria-label="Create new"
            >
              <Plus className={`fab-button-icon h-5 w-5 transition-transform duration-300 ${showBookingOptions ? 'rotate-45' : ''}`} />
            </button>
            

          </div>

          {/* Nexus AI Set Up Assistant button removed - now accessible via Help Centre */}

          {/* AI Voice Mode toggle — top of the stack. Off: blue. On: red (press to stop).
              Admin-only: hidden for resources unless they've been granted admin. */}
          {aiVoiceAllowed ? (
          <div
            className={`
              absolute left-1/2 transform -translate-x-1/2 transition-all duration-300 ease-in-out
              ${showBookingOptions
                ? 'opacity-0 scale-95 pointer-events-none invisible'
                : 'opacity-100 scale-100 pointer-events-auto visible'}
            `}
            style={{ bottom: `${voiceButtonBottomRem}rem` }}
          >
            <div className="relative h-14 w-14">
              {agentNotice && !onAgentsPage ? (
                <AgentSpeechBubble
                  notice={agentNotice}
                  onOpen={openAgentNotice}
                  onDismiss={dismissAgentNotice}
                  tail="right"
                  className="absolute right-[calc(100%+10px)] top-1/2 z-20 w-[220px] -translate-y-1/2"
                />
              ) : null}
              {AI_VOICE_FAN_ENABLED ? (
              <>
              <AiFanOption
                open={aiMenuOpen}
                side="left"
                x={-150}
                y={0}
                delayMs={0}
                icon={voiceOn ? <X className="h-5 w-5" /> : voiceOrb}
                label={voiceOn ? 'End voice' : 'Voice mode'}
                ariaLabel={voiceOn ? 'End the conversation with Nexus' : 'Talk to Nexus'}
                anchor="voice-mode-button"
                className={
                  voiceOn
                    ? 'bg-gradient-to-br from-rose-500 to-red-600'
                    : 'bg-gradient-to-br from-gray-800 to-black'
                }
                shadow={
                  voiceOn
                    ? '0 8px 24px rgba(225, 29, 72, 0.4)'
                    : '0 8px 24px rgba(17, 24, 39, 0.35)'
                }
                onClick={() => {
                  setAiMenuOpen(false);
                  handleVoiceToggle();
                }}
              />
              {agentsEnabled ? (
                <AiFanOption
                  open={aiMenuOpen}
                  side="left"
                  x={-78}
                  y={0}
                  delayMs={50}
                  icon={<NexusFace size={34} />}
                  label="Agents"
                  ariaLabel={agentMarkText ? `Open Agents. ${agentMarkText}` : 'Open Agents'}
                  mark={aiMenuOpen && visibleAgentMark ? <AgentWorkMark signal={visibleAgentMark} /> : null}
                  className="bg-gradient-to-br from-gray-800 to-black"
                  shadow="0 8px 24px rgba(17, 24, 39, 0.35)"
                  onClick={() => {
                    setAiMenuOpen(false);
                    dismissAgentNotice();
                    openAgents();
                  }}
                />
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setShowBookingOptions(false);
                  if (voiceOn) {
                    setAiMenuOpen(false);
                    handleVoiceToggle();
                    return;
                  }
                  setAiMenuOpen((open) => !open);
                }}
                onPointerDown={() => { if (!voiceOn) prewarmVoiceSession(); }}
                aria-label={`${voiceOn ? 'End the conversation with Nexus' : aiMenuOpen ? 'Close AI options' : 'Nexus AI'}${agentMarkOnButton && agentMarkText ? `. ${agentMarkText}` : ''}`}
                aria-expanded={voiceOn ? false : aiMenuOpen}
                aria-haspopup={voiceOn ? undefined : 'menu'}
                className={`relative flex h-14 w-14 items-center justify-center rounded-full text-white ${
                  voiceOn
                    ? 'bg-gradient-to-br from-rose-500 to-red-600'
                    : 'bg-gradient-to-br from-gray-800 to-black'
                }`}
                style={{
                  boxShadow: voiceOn
                    ? '0 8px 24px rgba(225, 29, 72, 0.4)'
                    : '0 8px 24px rgba(17, 24, 39, 0.35)',
                  transform: aiMenuOpen ? 'scale(0.92)' : 'scale(1)',
                  transition: `transform 380ms ${FAN_SPRING}, background-color 300ms ease`,
                }}
              >
                <span className="relative flex h-9 w-9 items-center justify-center">
                  <span
                    className="absolute inset-0 flex items-center justify-center"
                    style={{
                      opacity: aiMenuOpen ? 0 : 1,
                      transform: aiMenuOpen ? 'rotate(90deg) scale(0.5)' : 'rotate(0deg) scale(1)',
                      transition: `transform 380ms ${FAN_SPRING}, opacity 160ms ease`,
                    }}
                  >
                    <NexusFace
                      size={36}
                      pulse={voiceOn && (voiceStatus === 'connecting' || voiceProcessing)}
                    />
                  </span>
                  <span
                    className="absolute inset-0 flex items-center justify-center"
                    style={{
                      opacity: aiMenuOpen ? 1 : 0,
                      transform: aiMenuOpen ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.5)',
                      transition: `transform 380ms ${FAN_SPRING}, opacity 160ms ease`,
                    }}
                  >
                    <X className="h-6 w-6" />
                  </span>
                </span>
                {agentMarkOnButton && visibleAgentMark ? (
                  <AgentWorkMark signal={visibleAgentMark} />
                ) : voiceStatus === 'active' && !aiMenuOpen ? (
                  <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60" />
                    <span className="relative inline-flex h-3 w-3 rounded-full border-2 border-red-500 bg-white" />
                  </span>
                ) : null}
              </button>
              </>
              ) : (
                <button
                  type="button"
                  {...holdToTalk.handlers}
                  onPointerDown={(e) => {
                    if (onAgentsPage) return;
                    holdToTalk.handlers.onPointerDown(e);
                    headStartNexusCall();
                  }}
                  onClick={() => {
                    if (holdToTalk.consumeHold()) return;
                    setShowBookingOptions(false);
                    dismissAgentNotice();
                    if (openLiveChat()) return;
                    openAgents();
                  }}
                  aria-label={onAgentsPage
                    ? (agentMarkText ? `Agents. ${agentMarkText}` : 'Agents')
                    : (agentMarkText ? `Open Agents. ${agentMarkText}. Press and hold to talk to Nexus` : 'Open Agents. Press and hold to talk to Nexus')}
                  className="relative flex h-14 w-14 select-none items-center justify-center rounded-full bg-gradient-to-br from-gray-800 to-black text-white"
                  style={{ boxShadow: '0 8px 24px rgba(17, 24, 39, 0.35)', WebkitTouchCallout: 'none' }}
                >
                  <NexusFace size={36} />
                  {agentMarkOnButton && visibleAgentMark ? (
                    <AgentWorkMark signal={visibleAgentMark} />
                  ) : null}
                </button>
              )}
            </div>
          </div>
          ) : null}

          {/* Control Centre Button — native app only, sits above messages (or in the
              messages slot when messages is hidden so there's no gap). */}
          {isNativeApp && selectedBusiness && (
            <div className={`
              absolute ${messagesButtonVisible ? 'bottom-28' : 'bottom-14'} left-1/2 transform -translate-x-1/2 transition-all duration-300 ease-in-out
              ${showBookingOptions
                ? 'opacity-0 scale-95 pointer-events-none invisible'
                : 'opacity-100 scale-100 pointer-events-auto visible'}
            `}>
              <button
                onClick={() => navigate('/admin-dashboard')}
                aria-label={canViewAdminDashboard ? 'Control Centre' : 'Home'}
                className="backdrop-blur-xl bg-gradient-to-br from-gray-700 to-gray-900 border border-white/20 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 p-3.5 hover:scale-105"
                style={{
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  boxShadow: '0 8px 32px rgba(17, 24, 39, 0.35)'
                }}
              >
                <LayoutDashboard className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Permanent Messages Button - Mobile and Desktop */}
          {messagesButtonVisible && (
            <div className={`
              absolute bottom-14 left-1/2 transform -translate-x-1/2 transition-all duration-300 ease-in-out
              ${showBookingOptions
                ? 'opacity-0 scale-95 pointer-events-none invisible'
                : 'opacity-100 scale-100 pointer-events-auto visible'}
            `}>
              <div className="relative">
                <button
                  onClick={() => navigate('/messages')}
                  aria-label="Messages"
                  className="backdrop-blur-xl bg-gradient-to-br from-green-500 to-green-600 border border-white/20 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 p-3.5 hover:scale-105"
                  style={{ 
                    backdropFilter: 'blur(20px)', 
                    WebkitBackdropFilter: 'blur(20px)',
                    boxShadow: '0 8px 32px rgba(34, 197, 94, 0.3)'
                  }}
                >
                  <MessageSquare className="h-5 w-5" />
                </button>
                
                {/* Unread messages badge - includes unread messages and unviewed missed calls */}
                {(unreadMessagesCount + unviewedMissedCallsCount) > 0 && (
                  <div className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full shadow-lg border-2 border-white/20">
                    {(unreadMessagesCount + unviewedMissedCallsCount) > 99 ? '99+' : (unreadMessagesCount + unviewedMissedCallsCount)}
                  </div>
                )}
              </div>
            </div>
          )}

            </>
          )}

          {/* Permanent Calls Button - Mobile Only - REMOVED: Now handled by dial pad icon in top navigation */}

          {/* Nexus Controls - Removed from mobile FAB menu */}
          
          {/* Horizontal Navigation - REMOVED: No longer needed */}
          
          {/* Quick Actions — matches calendar / messages create popover */}
          <div
            className={`
            absolute bottom-full ${bottomBarActive ? 'right-2 mb-2' : 'right-0 mb-3'} w-[min(100vw-1.5rem,17.5rem)] transform transition-all duration-300 ease-in-out
            ${showBookingOptions
              ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto visible'
              : 'opacity-0 translate-y-2 scale-95 pointer-events-none invisible'}
          `}
          >
            <div
              className="overflow-hidden pointer-events-auto"
              style={bottomBarActive ? fabMenuShellStyleOpaque : fabMenuShellStyle}
              role="menu"
            >
              <div className="flex items-center gap-2 px-3.5 pt-3 pb-2">
                <div className="min-w-0 flex-1">
                  <p
                    className="text-[11px] font-semibold tracking-wide text-black/35"
                    style={{ letterSpacing: '0.02em' }}
                  >
                    Create
                  </p>
                  {compensationType === 'hourly' &&
                    showClockInFromFAB &&
                    clockedIn && (
                      <p className="mt-1 text-[13px] font-semibold tabular-nums text-black/70">
                        {elapsedTime}
                      </p>
                    )}
                </div>
              </div>

              <div className="mx-2.5 mb-2.5 overflow-hidden rounded-[10px] bg-black/[0.035] divide-y divide-black/[0.06]">
                {/* Timeclock — hourly resources only */}
                {compensationType === 'hourly' && showClockInFromFAB && (
                  <>
                    {clockedIn ? (
                      <>
                        <FabMenuRow
                          label={onBreak ? 'End Break' : 'Start Break'}
                          icon={Clock}
                          iconClassName={
                            onBreak
                              ? 'bg-[#FF9F0A] text-white shadow-sm shadow-[#FF9F0A]/30'
                              : 'bg-[#007AFF] text-white shadow-sm shadow-[#007AFF]/30'
                          }
                          onClick={() =>
                            gateProcedures(onBreak ? 'break_end' : 'break_start', handleBreak)
                          }
                        />
                        <FabMenuRow
                          label="Clock Out"
                          icon={X}
                          iconClassName="bg-[#FF3B30] text-white shadow-sm shadow-[#FF3B30]/30"
                          disabled={isClockBusy}
                          onClick={() => gateProcedures('clock_out', handleClockOut)}
                        />
                      </>
                    ) : (
                      <FabMenuRow
                        label="Clock In"
                        icon={Clock}
                        iconClassName="bg-[#34C759] text-white shadow-sm shadow-[#34C759]/30"
                        disabled={isClockBusy}
                        onClick={() => gateProcedures('clock_in', handleClockIn)}
                      />
                    )}
                  </>
                )}

                {/* Location Booking */}
                {canAccess.calendar &&
                  canAccess.createBookings &&
                  !isPageDisabled('calendarV2') &&
                  appSettingsLoaded &&
                  showLocationBookingInFAB && (
                    <FabMenuRow
                      label="Location Booking"
                      description="Create a location-based booking"
                      icon={MapPin}
                      onClick={() => {
                        setIsCreateBookingModalOpen(true);
                        setShowBookingOptions(false);
                      }}
                    />
                  )}

                {/* Mobile Booking */}
                {canAccess.calendar &&
                  canAccess.createBookings &&
                  !isPageDisabled('mobile-calendar') &&
                  appSettingsLoaded &&
                  showMobileBookingInFAB && (
                    <FabMenuRow
                      label="Mobile Booking"
                      description="Create a mobile service booking"
                      icon={Car}
                      onClick={() => {
                        setIsCreateMobileBookingModalOpen(true);
                        setShowBookingOptions(false);
                      }}
                    />
                  )}

                {/* Event (calendar time block) */}
                {canAccess.calendar &&
                  !isPageDisabled('calendarV2') &&
                  appSettingsLoaded &&
                  showTasksInFAB && (
                    <FabMenuRow
                      label="Event"
                      description="Create a calendar event"
                      icon={ClipboardList}
                      onClick={() => {
                        setIsCreateTaskModalOpen(true);
                        setShowBookingOptions(false);
                      }}
                    />
                  )}

                {/* Field Quote */}
                {canAccess.calendar &&
                  canAccess.createBookings &&
                  !isPageDisabled('calendarV2') &&
                  appSettingsLoaded &&
                  showFieldQuotesInFAB && (
                    <FabMenuRow
                      label="Field Quote"
                      description="Schedule a field quote"
                      icon={FileSearch}
                      onClick={() => {
                        setIsScheduleFieldQuoteFromFabOpen(true);
                        setShowBookingOptions(false);
                      }}
                    />
                  )}

                {/* Proposal */}
                {!isPageDisabled('proposals') &&
                  canAccess.proposals &&
                  appSettingsLoaded &&
                  showProposalInFAB && (
                    <FabMenuRow
                      label="Proposal"
                      description="Create a new proposal"
                      icon={FileText}
                      onClick={() => {
                        setIsCreateProposalModalOpen(true);
                        setShowBookingOptions(false);
                      }}
                    />
                  )}

                {/* Opportunity (CRM lead) — off by default, enabled in CRM app settings */}
                {!isPageDisabled('crm') &&
                  canAccess.crm &&
                  appSettingsLoaded &&
                  showOpportunityInFAB && (
                    <FabMenuRow
                      label="Opportunity"
                      description="Create a new CRM lead"
                      icon={Target}
                      onClick={() => {
                        if (aiCardScanEnabled) {
                          setIsLeadChoiceOpen(true);
                        } else {
                          setIsCreateOpportunityModalOpen(true);
                        }
                        setShowBookingOptions(false);
                      }}
                    />
                  )}

                {/* Sales Pin (door-to-door) — off by default, enabled in Marketing app settings */}
                {appSettingsLoaded && showSalesPinInFAB && (
                  <FabMenuRow
                    label="Sales Pin"
                    description="Drop a pin at your location"
                    icon={MapPin}
                    onClick={() => {
                      setIsSalesPinModalOpen(true);
                      setShowBookingOptions(false);
                    }}
                  />
                )}

                {/* Project */}
                {!isPageDisabled('projects') && appSettingsLoaded && showProjectInFAB && (
                  <FabMenuRow
                    label="Project"
                    description="Create a new project"
                    icon={Folder}
                    onClick={() => {
                      setIsCreateProjectModalOpen(true);
                      setShowBookingOptions(false);
                    }}
                  />
                )}

                {/* Withdraw Stock */}
                {!isPageDisabled('supplies') &&
                  appSettingsLoaded &&
                  showWithdrawStockInFAB && (
                    <FabMenuRow
                      label="Withdraw Stock"
                      description="Take stock from inventory"
                      icon={Package2}
                      onClick={() => {
                        setIsWithdrawStockModalOpen(true);
                        setShowBookingOptions(false);
                      }}
                    />
                  )}

                {/* Upload Expense */}
                {!isPageDisabled('expenses') &&
                  canAccess.expenses &&
                  canAccess.uploadExpense &&
                  appSettingsLoaded &&
                  showExpenseInFAB && (
                    <FabMenuRow
                      label="Upload Expense"
                      description="Add a new expense"
                      icon={Upload}
                      onClick={() => {
                        setIsAddExpenseModalOpen(true);
                        setShowBookingOptions(false);
                      }}
                    />
                  )}

                {/* Subscription */}
                {appSettingsLoaded && showSubscriptionsInFAB && (
                  <FabMenuRow
                    label="Subscription"
                    description="Create a subscription"
                    icon={RefreshCw}
                    onClick={() => {
                      setIsCreateSubscriptionModalOpen(true);
                      setShowBookingOptions(false);
                    }}
                  />
                )}

                {/* Complete a Document */}
                {documentsFabEnabled && canAccess.docsCanComplete && (
                  <FabMenuRow
                    label="Complete Document"
                    description="Fill out a document"
                    icon={FileText}
                    onClick={() => {
                      setShowDocumentPicker(true);
                      setShowBookingOptions(false);
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        
        </div>
      </div>

      {fabClusterHidden && agentNotice && !onAgentsPage ? (
        <AgentSpeechBubble
          notice={agentNotice}
          onOpen={openAgentNotice}
          onDismiss={dismissAgentNotice}
          className="fixed bottom-6 right-6 z-[95] w-[220px]"
        />
      ) : null}

      {showDocumentPicker && selectedBusiness?.id && (
        <DocumentPickerModal businessId={selectedBusiness.id} onClose={() => setShowDocumentPicker(false)} />
      )}

      {isCreateOpportunityModalOpen && (
        <CrmEmbeddedCreateOpportunityModal
          onClose={() => setIsCreateOpportunityModalOpen(false)}
        />
      )}

      <LeadCreateChoiceModal
        isOpen={isLeadChoiceOpen}
        onClose={() => setIsLeadChoiceOpen(false)}
        onCreate={() => {
          setIsLeadChoiceOpen(false);
          setIsCreateOpportunityModalOpen(true);
        }}
        onCreateFromPhoto={() => {
          setIsLeadChoiceOpen(false);
          setIsCardScanOpen(true);
        }}
      />

      {selectedBusiness?.id && (
        <BusinessCardScanModal
          isOpen={isCardScanOpen}
          businessId={selectedBusiness.id}
          onClose={() => setIsCardScanOpen(false)}
        />
      )}

      {selectedBusiness?.id && (
        <SalesPinDropModal
          isOpen={isSalesPinModalOpen}
          businessId={selectedBusiness.id}
          onClose={() => setIsSalesPinModalOpen(false)}
        />
      )}

      {activeProcedureTemplateId && (
        <DocumentFillModal
          key={activeProcedureTemplateId}
          templateId={activeProcedureTemplateId}
          onClose={handleProcedureClose}
          onCompleted={handleProcedureCompleted}
        />
      )}

      <CreateBookingModal
        isOpen={isCreateBookingModalOpen}
        initialCustomer={assistantInitialCustomer}
        initialProposal={assistantInitialProposal}
        onClose={() => {
          setIsCreateBookingModalOpen(false);
          setAssistantInitialCustomer(null);
          setAssistantInitialProposal(null);
        }}
        onSuccess={async () => {
          setIsCreateBookingModalOpen(false);
          setAssistantInitialCustomer(null);
          setAssistantInitialProposal(null);
          await handleSuccess();
        }}
      />

      <CreateProposalModal
        isOpen={isCreateProposalModalOpen}
        initialCustomer={assistantInitialCustomer}
        onClose={() => {
          setIsCreateProposalModalOpen(false);
          setAssistantInitialCustomer(null);
        }}
        onSuccess={async (createdProposal) => {
          setIsCreateProposalModalOpen(false);
          setAssistantInitialCustomer(null);
          await handleSuccess();
          
          // Navigate to proposals page and open the details modal
          if (createdProposal?.id) {
            navigate(`/proposals?proposalId=${createdProposal.id}`, {
              state: { selectedProposal: createdProposal }
            });
          }
        }}
      />

      <CreateProjectModal
        isOpen={isCreateProjectModalOpen}
        onClose={() => setIsCreateProjectModalOpen(false)}
        onSuccess={(projectId) => {
          setIsCreateProjectModalOpen(false);
          if (projectId) {
            navigate('/projects', { state: { openProjectId: projectId } });
          }
        }}
        navigateToProject={false}
      />

      <WithdrawStockModal
        isOpen={isWithdrawStockModalOpen}
        onClose={() => setIsWithdrawStockModalOpen(false)}
        onSuccess={async () => {
          // Don't close modal - allow multiple withdrawals in one session
          await handleSuccess();
        }}
      />

      {/* Upload Receipt Modal */}
      {isAddExpenseModalOpen && isExpensesAppInstalled && selectedBusiness?.id && (
        <UploadReceiptModalWrapper
          isOpen={isAddExpenseModalOpen}
          onClose={() => setIsAddExpenseModalOpen(false)}
          businessId={selectedBusiness.id}
          onSwitchToManual={(preselection) => {
            setIsAddExpenseModalOpen(false);
            setManualExpensePreselection(preselection ?? null);
            setIsManualExpenseModalOpen(true);
          }}
        />
      )}

      {/* Manual Expense Modal (entry point for "Manual" from the mobile camera view) */}
      {isManualExpenseModalOpen && isExpensesAppInstalled && selectedBusiness?.id && (
        <AddManualExpenseModalWrapper
          isOpen={isManualExpenseModalOpen}
          onClose={() => {
            setIsManualExpenseModalOpen(false);
            setManualExpensePreselection(null);
          }}
          businessId={selectedBusiness.id}
          initialBookingId={manualExpensePreselection?.bookingId || undefined}
          initialProjectId={manualExpensePreselection?.projectId || undefined}
        />
      )}

      <CreateSubscriptionModal
        isOpen={isCreateSubscriptionModalOpen}
        onClose={() => setIsCreateSubscriptionModalOpen(false)}
        onSuccess={async () => {
          setIsCreateSubscriptionModalOpen(false);
          await handleSuccess();
        }}
      />

      <CreateMobileBookingModal
        isOpen={isCreateMobileBookingModalOpen}
        initialCustomer={assistantInitialCustomer}
        onClose={() => {
          setIsCreateMobileBookingModalOpen(false);
          setAssistantInitialCustomer(null);
        }}
        onSuccess={async () => {
          setIsCreateMobileBookingModalOpen(false);
          setAssistantInitialCustomer(null);
          await handleSuccess();
        }}
      />

      <CreateTaskModal
        isOpen={isCreateTaskModalOpen}
        initialCustomer={assistantInitialCustomer}
        onClose={() => {
          setIsCreateTaskModalOpen(false);
          setAssistantInitialCustomer(null);
        }}
        onSuccess={async () => {
          setIsCreateTaskModalOpen(false);
          setAssistantInitialCustomer(null);
          await handleSuccess();
        }}
      />

      <ScheduleFieldQuoteModal
        isOpen={isScheduleFieldQuoteFromFabOpen}
        onClose={() => setIsScheduleFieldQuoteFromFabOpen(false)}
        projectId={null}
        projectTitle={null}
        onSuccess={async () => {
          setIsScheduleFieldQuoteFromFabOpen(false);
          await handleSuccess();
        }}
      />

      {/* HorizontalNavConfigModal - REMOVED */}

      {/* First-use gate for AI Help / Voice: intro → free tokens → terms */}
      <NexusVoiceIntroModal
        isOpen={showVoiceIntro}
        onClose={() => { setShowVoiceIntro(false); setAiPendingAction(null); }}
        onAccepted={handleAiOnboardingAccepted}
        businessId={selectedBusiness?.id}
        welcomeAlreadyClaimed={aiWelcomeClaimed}
        termsAlreadyAccepted={aiTermsAccepted}
      />

      {/* Nexus Overlay - Admin Only */}
      {isAdmin && (
        <NexusOverlay 
          isOpen={isNexusOverlayOpen} 
          onClose={() => setIsNexusOverlayOpen(false)}
          stopSpeechRef={stopSpeechRef}
          onBookingUpdate={handleSuccess}
        />
      )}

      {/* Active Timer Warning Modal */}
      <ActiveJobTimerBeforeActionModal
        isOpen={fabActiveJobTimerModalOpen}
        onClose={() => {
          if (!fabActionModalLoading) {
            setFabActiveJobTimerModalOpen(false);
            setFabPendingAction(null);
            setFabActiveJobTimerEntries([]);
          }
        }}
        actionType={fabPendingAction ?? 'clock_out'}
        activeTimerCount={fabActiveJobTimerEntries.length}
        onStopAndProceed={handleStopTimersAndProceedFAB}
        loading={fabActionModalLoading}
      />
    </div>
  );
} 